import useUrlSearchState from "@/hooks/useUrlSearchState";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Calendar, MapPin, Clock, Ticket, Info, Tag, Check, Gamepad2, Trophy, Users, ExternalLink, ChevronLeft, Eye, Minus, Plus, Share2, PlusCircle, Pencil, BarChart3, Copy, CreditCard, QrCode, Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import EventSalesDashboard from "@/components/admin/EventSalesDashboard";
import { EventEditorDialog } from "@/components/admin/EventEditorDialog";
import ShareDialog from "@/components/share/ShareDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useCMSPage, SectionRenderer } from "@/components/cms/DynamicPage";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/userAccess";
import { usePlatform } from "@/contexts/PlatformContext";
import { SEO } from "@/components/SEO";
import { formatCPFBR, formatDateBR, formatPhoneBR } from "@/utils/formatDate";
import { isValidCPF, isValidEmail, isValidPhoneBR } from "@/utils/validation";
import useCheckoutRealtime from "@/hooks/useCheckoutRealtime";
import { getCuradoriaTitles, getCuradoriaVisuals, normalizeCuradorias, resolveCuradoriaForEvent } from "@/lib/curadoriaSettings";
import { isOnlineGameEvent } from "@/lib/eventClassification";
import { buildMetaCheckoutPayload, trackMetaEvent } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENTS_BATCH_SIZE = 6;
const SITE_URL = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || '';
const isEventCancelled = (event) => ["cancelled", "canceled"].includes(String(event?.status || "").toLowerCase());
const formatCurrencyValue = (value) => Number(value || 0).toFixed(2);
const formatCurrencyBR = (value) => formatCurrencyValue(value).replace(".", ",");
const buildEventSeoDescription = (event) => {
  const title = String(event?.title || "Evento do Lord of the Quiz").trim();
  const description = String(event?.description || "").replace(/\s+/g, " ").trim();
  return (description
    ? `${title}: ${description}`
    : `Participe de ${title}, um Quiz Show do Lord of the Quiz. Garanta seu ingresso e reúna sua equipe.`
  ).slice(0, 200);
};
const formatCountdown = (milliseconds) => {
  const totalSeconds = Math.max(0, Math.floor((milliseconds || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};
const getApiErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => item?.msg || item?.message)
      .filter((message) => typeof message === "string" && message.trim());
    if (messages.length) return messages.join(" ");
  }
  if (detail && typeof detail === "object") {
    const message = detail.msg || detail.message;
    if (typeof message === "string" && message.trim()) return message;
  }

  return fallback;
};
const normalizeQuantityPricingRules = (rules = []) =>
  [...(rules || [])]
    .map((rule) => ({
      min_quantity: Math.max(1, parseInt(rule?.min_quantity, 10) || 1),
      rule_type: rule?.rule_type === "fixed_discount"
        ? "fixed_discount"
        : rule?.rule_type === "fixed_price"
          ? "fixed_price"
          : "percent_discount",
      value: Math.max(0, Number(rule?.value) || 0),
      label: rule?.label || "",
    }))
    .sort((a, b) => a.min_quantity - b.min_quantity);

const applyQuantityPricing = (basePrice, quantity, rules = []) => {
  const normalizedBasePrice = Number(basePrice || 0);
  const normalizedQuantity = Math.max(1, parseInt(quantity, 10) || 1);
  const normalizedRules = normalizeQuantityPricingRules(rules);

  let appliedRule = null;
  let unitPrice = normalizedBasePrice;

  normalizedRules.forEach((rule) => {
    if (normalizedQuantity < rule.min_quantity) return;
    const candidatePrice = rule.rule_type === "fixed_price"
      ? Number(rule.value || 0)
      : rule.rule_type === "fixed_discount"
        ? Math.max(0, normalizedBasePrice - (Number(rule.value || 0)))
      : Math.max(0, normalizedBasePrice - (normalizedBasePrice * Number(rule.value || 0) / 100));
    if (!appliedRule || candidatePrice < unitPrice) {
      appliedRule = rule;
      unitPrice = candidatePrice;
    }
  });

  return {
    baseUnitPrice: normalizedBasePrice,
    unitPrice,
    appliedRule,
    rules: normalizedRules,
  };
};

const describeQuantityPricingRule = (rule) => {
  if (!rule) return "";
  if (rule.rule_type === "fixed_discount") {
    return `${rule.min_quantity}+ ingressos: Desconto de R$ ${formatCurrencyBR(rule.value)} por ingresso`;
  }
  if (rule.rule_type === "fixed_price") {
    return `${rule.min_quantity}+ ingressos por R$ ${formatCurrencyBR(rule.value)} cada`;
  }
  return `${rule.min_quantity}+ ingressos: ${Number(rule.value || 0).toFixed(0)}% OFF`;
};

const describeAppliedQuantityPricing = (rule, unitPrice) => {
  if (!rule) return "";
  return `${rule.min_quantity}+ ingressos: R$ ${formatCurrencyBR(unitPrice)} por ingresso`;
};

const hasActiveSubscription = (user) =>
  !!user && user.subscription_plan !== "free" && ["active", "trialing"].includes(user.subscription_status);

const getSubscriptionBenefitPreview = ({ user, selectedTicketType, quantity, creditsToApply, couponResult, unitPrice, subtotal }) => {
  if (!user || !selectedTicketType) return null;

  const rules = user.benefit_rules || {};
  if (rules.ticket_benefit_mode !== "first_lot_price") return null;
  if (!hasActiveSubscription(user)) return null;
  if (couponResult && !rules.allow_benefit_with_coupon) return null;
  if (creditsToApply > 0 && !rules.allow_benefit_with_free_ticket) return null;

  const firstLotBasePrice = Number(selectedTicketType.first_lot_price ?? unitPrice);
  const currentUnitPrice = Number(unitPrice || 0);
  if (firstLotBasePrice <= 0) return null;

  const firstLotQuantityPricing = applyQuantityPricing(
    firstLotBasePrice,
    quantity,
    selectedTicketType.quantity_pricing
  );
  const firstLotEquivalentPrice = Number(firstLotQuantityPricing.unitPrice || firstLotBasePrice);

  if (firstLotEquivalentPrice >= currentUnitPrice) return null;

  const discount = Math.min(currentUnitPrice - firstLotEquivalentPrice, subtotal);
  if (discount <= 0) return null;

  return {
    label: selectedTicketType.first_lot_label || "Preço garantido de 1º lote",
    discount,
    firstLotPrice: firstLotEquivalentPrice,
    currentUnitPrice,
    appliesToQuantity: Math.min(1, Math.max(0, Number(quantity || 0))),
  };
};

function AppEventsPage({
  displayEvents,
  eventSearch,
  getCuradoriaLabel,
  getCuradoriaMeta,
  handleEventClick,
  handleOpenBooking,
  handleOpenEdit,
  isAdmin,
  isEventLastTickets,
  isEventSoldOut,
  isPresentialEventBookable,
  navigate,
  visibleEvents,
  renderOnlinePrimaryAction,
  selectedCuradorias,
  showPastEvents,
  setEventSearch,
  togglePastEvents,
  toggleCuradoriaFilter,
  hasMoreEvents,
  isLoadingMoreEvents,
  curadorias,
}) {
  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-24" data-testid="app-events-page">
      <div className="mx-auto flex max-w-md flex-col gap-4 px-4">
        {isAdmin ? (
          <Button
            onClick={() => navigate("/admin")}
            className="bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c]"
            data-testid="create-event-btn"
          >
            <PlusCircle className="mr-2 h-4 w-4" /> Criar Evento
          </Button>
        ) : null}

        <section className="rounded-[22px] border border-white/10 bg-zinc-950/75 p-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <Input
              value={eventSearch}
              onChange={(e) => setEventSearch(e.target.value)}
              placeholder="Buscar por nome, tema ou local"
              className="border-white/10 bg-zinc-900/60 pl-10 text-white placeholder:text-zinc-500"
              data-testid="events-search-input"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {getCuradoriaTitles(curadorias).map((curadoria) => {
              const active = selectedCuradorias.includes(curadoria);
              return (
                <button
                  key={curadoria}
                  type="button"
                  onClick={() => toggleCuradoriaFilter(curadoria)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] ${
                    active
                      ? "border-[#fccf1f] bg-[#fccf1f] text-black"
                      : "border-white/10 bg-white/[0.03] text-zinc-300"
                  }`}
                >
                  {curadoria}
                </button>
              );
            })}
            <button
              type="button"
              onClick={togglePastEvents}
              className={`rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] ${
                showPastEvents
                  ? "border-[#fccf1f] bg-[#fccf1f] text-black"
                  : "border-white/10 bg-white/[0.03] text-zinc-300"
              }`}
              data-testid="events-past-filter"
            >
              Eventos Passados
            </button>
          </div>
        </section>

        <section className="space-y-3">
          {visibleEvents.map((event) => (
            <div key={event.id} className="overflow-hidden rounded-[22px] border border-white/10 bg-zinc-950/80">
              {event.image_url ? (
                <div className="relative h-36 overflow-hidden">
                  <img
                    src={event.image_url}
                    alt={event.title}
                    className={`h-full w-full object-cover ${event.is_past || isEventCancelled(event) ? "grayscale" : ""}`}
                  />
                  {isEventCancelled(event) ? (
                    <span className="absolute left-3 top-3 inline-flex rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-black tracking-[0.18em] text-white">
                      CANCELADO
                    </span>
                  ) : isEventSoldOut(event) ? (
                    <span className="absolute left-3 top-3 inline-flex rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-black tracking-[0.18em] text-white">
                      ESGOTADO
                    </span>
                  ) : isEventLastTickets(event) ? (
                    <span className="absolute left-3 top-3 inline-flex rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-black tracking-[0.18em] text-white shadow-lg shadow-red-950/40">
                      ÚLTIMOS INGRESSOS!
                    </span>
                  ) : null}
                </div>
              ) : null}

              <div className="space-y-3 p-4">
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-black tracking-[0.16em] ${
                    event.event_type === "online"
                      ? "bg-purple-500/20 text-purple-300"
                      : "bg-emerald-500/20 text-emerald-300"
                  }`}>
                    {event.event_type === "online" ? <Gamepad2 className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                    {event.event_type === "online" ? "ONLINE" : "PRESENCIAL"}
                  </span>
                  {getCuradoriaMeta(event) ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] font-bold text-zinc-200">
                      <Tag className="h-3 w-3" />
                      {getCuradoriaLabel(event)}
                    </span>
                  ) : null}
                </div>

                <div>
                  <h2 className="font-nunito text-xl font-black text-white">{event.title}</h2>
                  <div className="mt-2 space-y-1 text-sm text-zinc-400">
                    <p className="flex items-center gap-2"><Calendar className="h-4 w-4" /> {formatDateBR(event.date)}</p>
                    {event.time ? <p className="flex items-center gap-2"><Clock className="h-4 w-4" /> {event.time}</p> : null}
                    {event.venue?.name || event.location || event.venue?.city || event.city ? (
                      <p className="flex items-start gap-2">
                        <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>
                          {event.venue?.name || event.location || "Local a confirmar"}
                          {(event.venue?.city || event.city) && <><br />{event.venue?.city || event.city}</>}
                        </span>
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div>
                    {event.min_ticket_price > 0 ? (
                      <>
                        <p className="font-outfit text-[11px] uppercase tracking-[0.14em] text-zinc-500">a partir de</p>
                        <p className="font-nunito text-lg font-black text-[#fccf1f]">R$ {formatCurrencyBR(event.min_ticket_price)}</p>
                      </>
                    ) : event.event_type === "online" && !event.requires_ticket ? (
                      <p className="font-outfit text-sm text-emerald-300">Gratuito</p>
                    ) : null}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEventClick(event)}
                      variant="outline"
                      className="border-white/10 px-3 text-zinc-200 hover:bg-white/5"
                    >
                      <Eye className="mr-1 h-4 w-4" /> Ver
                    </Button>
                    {isAdmin ? (
                      <Button
                        onClick={() => handleOpenEdit(event)}
                        variant="outline"
                        className="border-orange-600/40 px-3 text-orange-300 hover:bg-orange-600/10"
                      >
                        <Pencil className="mr-1 h-4 w-4" /> Editar
                      </Button>
                    ) : null}
                    {event.event_type === "online" ? (
                      renderOnlinePrimaryAction(event, { compact: true })
                    ) : isPresentialEventBookable(event) ? (
                      <Button
                        onClick={() => handleOpenBooking(event)}
                        className="bg-[#fccf1f] px-3 text-black font-bold hover:bg-[#e6bc1c]"
                      >
                        Comprar
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {visibleEvents.length === 0 ? (
            <div className="rounded-[22px] border border-white/10 bg-zinc-950/70 p-5 text-center text-sm text-zinc-400">
              {eventSearch.trim()
                ? "Nenhum evento encontrado para essa busca."
                : "Nenhum evento disponível no momento. Volte em breve."}
            </div>
          ) : null}
        </section>

        {displayEvents.length > 0 ? (
          <div className="flex min-h-12 items-center justify-center py-2 text-center">
            {isLoadingMoreEvents ? (
              <span className="inline-flex items-center gap-2 font-outfit text-sm text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin text-[#fccf1f]" /> Carregando mais eventos...
              </span>
            ) : hasMoreEvents ? (
              <span className="font-outfit text-xs uppercase tracking-[0.16em] text-zinc-600">Role para carregar mais eventos</span>
            ) : (
              <span className="font-outfit text-xs text-zinc-600">Todos os eventos foram carregados.</span>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function EventsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: urlSlug, resumeToken } = useParams();
  const { isApp } = usePlatform();
  const [events, setEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]); // All events including past
  const [visibleEventsCount, setVisibleEventsCount] = useState(EVENTS_BATCH_SIZE);
  const [isLoadingMoreEvents, setIsLoadingMoreEvents] = useState(false);
  const loadMoreTimerRef = useRef(null);
  const [eventSearch, setEventSearch] = useUrlSearchState("q", "");
  const [curadorias, setCuradorias] = useState(normalizeCuradorias());
  const [selectedCuradorias, setSelectedCuradorias] = useUrlSearchState("categoria", []);
  const [showPastEvents, setShowPastEvents] = useUrlSearchState("passados", false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const bookingQueryAttemptRef = useRef(null);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [selectedTicketType, setSelectedTicketType] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    name: "",
    email: "",
    phone: "",
    quantity: 1,
    has_pcd: false,
    accessibility_needs: "",
    name_individually: false,
    attendees: [],
  });
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponResult, setCouponResult] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [creditsToApply, setCreditsToApply] = useState(0);
  const [teamCreditOptions, setTeamCreditOptions] = useState([]);
  const [teamCreditUses, setTeamCreditUses] = useState({});
  const [remainingCreditsForEvent, setRemainingCreditsForEvent] = useState(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [shareEvent, setShareEvent] = useState(null);
  const [showEventSales, setShowEventSales] = useState(null);
  const [detailsEvent, setDetailsEvent] = useState(null);
  const [showEditEvent, setShowEditEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [venues, setVenues] = useState([]);
  const [ownedOnlineEventIds, setOwnedOnlineEventIds] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  // Transparent checkout states
  const [paymentStep, setPaymentStep] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null); // 'card' or 'pix'
  const [pendingBookingId, setPendingBookingId] = useState(null);
  const [pendingBookingTotal, setPendingBookingTotal] = useState(null);
  const [cardForm, setCardForm] = useState({
    card_number: "",
    card_expiry: "",
    card_cvv: "",
    card_holder_name: "",
    cpf: "",
    postal_code: "",
    address_number: "",
    installments: 1,
  });
  const [pixData, setPixData] = useState(null); // { qr_code_image, qr_code_payload, payment_id, payment_expires_at }
  const [pixPolling, setPixPolling] = useState(false);
  const [pixTimeLeftMs, setPixTimeLeftMs] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const trackedPaymentMethodsRef = useRef(new Set());
  const trackedBrowserPurchasesRef = useRef(new Set());
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const bookingEmailError = bookingForm.email && !isValidEmail(bookingForm.email) ? "Digite um e-mail válido." : "";
  const bookingPhoneError = bookingForm.phone && !isValidPhoneBR(bookingForm.phone) ? "Digite um telefone válido com DDD." : "";
  const cardCpfError = cardForm.cpf && !isValidCPF(cardForm.cpf) ? "Digite um CPF válido." : "";

  const resetVisibleEvents = useCallback(() => {
    if (loadMoreTimerRef.current) {
      window.clearTimeout(loadMoreTimerRef.current);
      loadMoreTimerRef.current = null;
    }
    setIsLoadingMoreEvents(false);
    setVisibleEventsCount(EVENTS_BATCH_SIZE);
  }, []);
  
  const canUseTicketCredit = (user?.ticket_credits || 0) > 0;
  const perEventCreditLimit = user?.ticket_credits_per_event_limit;
  const normalizedPerEventCreditLimit =
    perEventCreditLimit === null || perEventCreditLimit === undefined || perEventCreditLimit === ""
      ? null
      : Math.max(0, Number(perEventCreditLimit) || 0);
  const normalizedRemainingCreditsForEvent =
    remainingCreditsForEvent === null || remainingCreditsForEvent === undefined || remainingCreditsForEvent === ""
      ? null
      : Math.max(0, Number(remainingCreditsForEvent) || 0);
  const maxCredits = canUseTicketCredit
    ? Math.min(
        user.ticket_credits,
        parseInt(bookingForm.quantity) || 1,
        normalizedRemainingCreditsForEvent ?? Number.POSITIVE_INFINITY,
        normalizedPerEventCreditLimit ?? Number.POSITIVE_INFINITY,
      )
    : 0;
  const teamCreditsToApply = Object.values(teamCreditUses).reduce((total, value) => total + Math.max(0, Number(value) || 0), 0);
  const totalCreditsToApply = creditsToApply + teamCreditsToApply;
  const canApplyTicketCredit = canUseTicketCredit && maxCredits > 0;
  
  // CMS page data
  const { page: cmsPage } = useCMSPage('eventos');
  const bookingEventIdFromQuery = new URLSearchParams(location.search).get("booking");
  const bookingReturnTo = new URLSearchParams(location.search).get("return_to");
  const heroSection = cmsPage?.sections?.find(s => s.type === 'hero' && s.visible !== false);
  const normalizedHeroSection = heroSection
    ? {
        ...heroSection,
        content: {
          ...heroSection.content,
          backgroundImage: undefined,
          overlay: "transparent",
          minHeight: "auto",
        },
        style: {
          ...heroSection.style,
          backgroundColor: "transparent",
          paddingTop: "8rem",
          paddingBottom: "1rem",
        },
      }
    : null;

  // Fetch event by slug when URL has slug parameter
  const openEventBySlug = useCallback(async (slug) => {
    try {
      const res = await axios.get(`${API}/events/by-slug/${slug}`);
      if (res.data) {
        setDetailsEvent(res.data);
        setShowEventDetails(true);
      }
    } catch (error) {
      console.error("Event not found:", slug);
      // Redirect to /eventos if event not found
      navigate('/eventos', { replace: true });
    }
  }, [navigate]);

  const fetchEvents = useCallback(async () => {
    try {
      // The public calendar needs both upcoming and past records to power its
      // local filter. Request beyond the default page so older history never
      // pushes upcoming events out of the response.
      const response = await axios.get(`${API}/events?include_past=true&limit=500`);
      const now = new Date();
      const isEventPast = (event) => {
        if (!event) return false;
        if (event.event_type === "online" && event.online_end_datetime) {
          return new Date(event.online_end_datetime) <= now;
        }
        if (event.date) {
          const endTime = event.end_time || event.time || "23:59";
          return new Date(`${event.date}T${endTime}`) < now;
        }
        return false;
      };

      const presentialEvents = (response.data || []).filter((event) => event.event_type === "presencial");

      const futureEvents = presentialEvents
        .filter((event) => !isEventPast(event))
        .map((event) => ({ ...event, is_past: false }));

      const pastEvents = presentialEvents
        .filter((event) => isEventPast(event))
        .map((event) => ({ ...event, is_past: true }));

      futureEvents.sort((a, b) => new Date(a.date || a.online_start_datetime) - new Date(b.date || b.online_start_datetime));
      pastEvents.sort((a, b) => new Date(b.date || b.online_start_datetime) - new Date(a.date || a.online_start_datetime));

      setEvents(futureEvents);
      setAllEvents({ future: futureEvents, past: pastEvents });
      resetVisibleEvents();
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Erro ao carregar eventos");
    }
  }, [resetVisibleEvents]);

  const fetchCuradorias = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/suggestions/curadorias`);
      setCuradorias(normalizeCuradorias(response.data));
    } catch (error) {
      console.error("Error fetching curadorias:", error);
    }
  }, []);

  const fetchTicketTypes = useCallback(async (eventId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/events/${eventId}/ticket-types`, token ? {
        headers: { Authorization: `Bearer ${token}` }
      } : undefined);
      const normalizedTicketTypes = (response.data.ticket_types || []).map((ticketType) => ({
        ...ticketType,
        quantity_pricing: normalizeQuantityPricingRules(ticketType.quantity_pricing),
      }));
      setRemainingCreditsForEvent(response.data?.ticket_credit_context?.remaining_for_event ?? null);
      setTicketTypes(normalizedTicketTypes);
      if (normalizedTicketTypes.length > 0) {
        setSelectedTicketType(normalizedTicketTypes[0]);
      } else {
        setSelectedTicketType(null);
      }
      return normalizedTicketTypes;
    } catch (error) {
      console.error("Error fetching ticket types:", error);
      toast.error("Erro ao carregar tipos de ingresso");
      return [];
    }
  }, []);

  const calculatePricing = useCallback(() => {
    if (!selectedTicketType) return;

    const quantityPricing = applyQuantityPricing(
      Number(selectedTicketType.price || 0),
      bookingForm.quantity,
      selectedTicketType.quantity_pricing
    );
    const unitPrice = quantityPricing.unitPrice;
    const quantity = parseInt(bookingForm.quantity);
    const subtotal = unitPrice * quantity;
    const baseSubtotal = quantityPricing.baseUnitPrice * quantity;
    const quantitySavings = Math.max(0, baseSubtotal - subtotal);

    const subscriptionBenefit = getSubscriptionBenefitPreview({
      user,
      selectedTicketType,
      quantity,
      creditsToApply: totalCreditsToApply,
      couponResult,
      unitPrice,
      subtotal,
    });
    const subscriptionBenefitDiscount = Number(subscriptionBenefit?.discount || 0);
    const subtotalAfterSubscriptionBenefit = Math.max(0, subtotal - subscriptionBenefitDiscount);

    const creditDiscount = unitPrice * totalCreditsToApply;
    let paidSubtotal = subtotalAfterSubscriptionBenefit - creditDiscount;

    let couponDiscount = 0;
    if (couponResult && paidSubtotal > 0) {
      const coupon = couponResult.coupon;
      if (coupon.discount_type === "percentage") {
        couponDiscount = paidSubtotal * (coupon.discount_value / 100);
      } else {
        couponDiscount = Math.min(coupon.discount_value, paidSubtotal);
      }
      couponDiscount = Math.round(couponDiscount * 100) / 100;
      paidSubtotal = Math.round((paidSubtotal - couponDiscount) * 100) / 100;
    }

    const feePercentage = Number(selectedTicketType.fee_percentage || 0);
    const feeFixed = Number(selectedTicketType.fee_fixed || 0);
    let percentageFee, totalFees, total;
    if (paidSubtotal > 0) {
      percentageFee = Math.round(paidSubtotal * (feePercentage / 100) * 100) / 100;
      totalFees = Math.round((percentageFee + feeFixed) * 100) / 100;
      total = Math.round((paidSubtotal + totalFees) * 100) / 100;
    } else {
      percentageFee = 0;
      totalFees = 0;
      total = 0;
    }

    const feeLabelParts = [];
    if (feePercentage > 0) feeLabelParts.push(`${feePercentage.toFixed(2)}%`);
    if (feeFixed > 0) feeLabelParts.push(`R$ ${feeFixed.toFixed(2)}`);

    setPricing({
      baseUnitPrice: quantityPricing.baseUnitPrice,
      baseSubtotal: baseSubtotal.toFixed(2),
      unitPrice,
      quantity,
      quantityPricingRule: quantityPricing.appliedRule,
      quantityPricing: quantityPricing.rules,
      quantitySavings: quantitySavings.toFixed(2),
      subtotal: subtotal.toFixed(2),
      subscriptionBenefitApplied: !!subscriptionBenefit,
      subscriptionBenefitLabel: subscriptionBenefit?.label || "",
      subscriptionBenefitDiscount: subscriptionBenefitDiscount.toFixed(2),
      subscriptionBenefitFirstLotPrice: Number(subscriptionBenefit?.firstLotPrice || 0).toFixed(2),
      subscriptionBenefitCurrentUnitPrice: Number(subscriptionBenefit?.currentUnitPrice || 0).toFixed(2),
      subtotalAfterSubscriptionBenefit: subtotalAfterSubscriptionBenefit.toFixed(2),
      creditDiscount: creditDiscount.toFixed(2),
      couponDiscount: couponDiscount.toFixed(2),
      paidSubtotal: paidSubtotal.toFixed(2),
      feePercentage: feePercentage.toFixed(2),
      percentageFee: percentageFee.toFixed(2),
      fixedFee: feeFixed.toFixed(2),
      feeLabel: feeLabelParts.join(" + "),
      fees: totalFees.toFixed(2),
      total: total.toFixed(2)
    });
  }, [bookingForm.quantity, couponResult, totalCreditsToApply, selectedTicketType, user]);

  const handleOpenBooking = useCallback((event) => {
    if (isEventCancelled(event)) {
      toast.error("Este evento foi cancelado.");
      return;
    }
    // Special and Premium games use the Passes de Jogo wallet in /jogos.
    // Do not create a booking, ticket PDF or check-in record for them.
    if (isOnlineGameEvent(event) && ["special", "premium"].includes(event?.catalog_section)) {
      const queryKey = event.catalog_section === "special" ? "specialGame" : "premiumGame";
      navigate(`/jogos?${queryKey}=${encodeURIComponent(event.id)}`);
      return;
    }

    // Registra a intenção no clique, antes de qualquer chamada ou redirecionamento
    // que possa descarregar a página e interromper o Pixel.
    trackMetaEvent(
      "InitiateCheckout",
      buildMetaCheckoutPayload(event, { value: Number(event?.price || 0), quantity: 1 }),
      `booking-start:${event.id}:${Date.now()}`,
    );
    setSelectedEvent(event);
    fetchTicketTypes(event.id);
    fetchTeamCreditOptions();
    setShowBookingDialog(true);
    setBookingForm({
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      quantity: 1,
      has_pcd: false,
      accessibility_needs: "",
      name_individually: false,
      attendees: []
    });
    setPricing(null);
    setCouponCode("");
    setCouponResult(null);
    setCreditsToApply(0);
    setTeamCreditUses({});
    setTeamCreditOptions([]);
    setRemainingCreditsForEvent(null);
  }, [fetchTicketTypes, navigate, user?.email, user?.name, user?.phone]);

  useEffect(() => {
    if (!showEventDetails || !detailsEvent?.id) return;
    const lowestPrice = (detailsEvent.ticket_tiers || [])
      .map((tier) => Number(tier?.price || 0))
      .filter((price) => price > 0)
      .sort((first, second) => first - second)[0] || Number(detailsEvent.price || 0);
    trackMetaEvent("ViewContent", buildMetaCheckoutPayload(detailsEvent, { value: lowestPrice }), `view:${detailsEvent.id}`);
  }, [detailsEvent, showEventDetails]);

  const fetchTeamCreditOptions = async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    try {
      const response = await axios.get(`${API}/team-subscriptions/credit-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeamCreditOptions(response.data?.members || []);
    } catch {
      setTeamCreditOptions([]);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchCuradorias();
    // Fetch venues for edit form
    if (isAdmin) {
      axios.get(`${API}/venues`).then(res => setVenues(res.data)).catch(() => {});
    }
  }, [fetchCuradorias, fetchEvents, isAdmin]);

  useEffect(() => {
    const curadoriaTitles = getCuradoriaTitles(curadorias);
    setSelectedCuradorias((prev) => {
      if (!curadoriaTitles.length) return [];
      return prev.filter((item) => curadoriaTitles.includes(item));
    });
  }, [curadorias, setSelectedCuradorias]);
  
  // Handle URL slug - open event modal on direct access or page refresh
  useEffect(() => {
    if (urlSlug && !showEventDetails && !showBookingDialog && events.length > 0) {
      // First check if event is in loaded events
      const eventInList = events.find(e => e.slug === urlSlug);
      if (eventInList) {
        setDetailsEvent(eventInList);
        setShowEventDetails(true);
      } else {
        // Fetch from API
        openEventBySlug(urlSlug);
      }
    }
  }, [urlSlug, events, openEventBySlug, showEventDetails, showBookingDialog]);

  useEffect(() => {
    if (!bookingEventIdFromQuery || showBookingDialog) return;

    const targetEvent = events.find(
      (event) => String(event.id) === String(bookingEventIdFromQuery)
    );

    if (targetEvent) {
      handleOpenBooking(targetEvent);
      return;
    }

    // Special games are intentionally shown in /jogos rather than the event
    // calendar, so retrieve their event shell directly for the shared checkout.
    if (bookingQueryAttemptRef.current === bookingEventIdFromQuery) return;
    bookingQueryAttemptRef.current = bookingEventIdFromQuery;
    let cancelled = false;
    axios.get(`${API}/events/${bookingEventIdFromQuery}`)
      .then((response) => {
        if (!cancelled && response.data) handleOpenBooking(response.data);
      })
      .catch((error) => {
        console.error("Error loading checkout event from query:", error);
        if (!cancelled) toast.error("Não foi possível carregar o ingresso deste jogo.");
      });
    return () => { cancelled = true; };
  }, [bookingEventIdFromQuery, events, handleOpenBooking, showBookingDialog]);

  useEffect(() => {
    if (!resumeToken) return;

    let cancelled = false;
    const loadAbandonedCheckout = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/bookings/resume/${resumeToken}`);
        if (cancelled) return;

        const booking = response.data.booking;
        const event = response.data.event;
        const types = await fetchTicketTypes(event.id);
        if (cancelled) return;

        const matchedType = (types || []).find((ticketType) => (
          ticketType.type === booking.ticket_type
          || ticketType.id === booking.ticket_type
          || ticketType.catalog_ticket_type_id === booking.catalog_ticket_type_id
        ));

        setSelectedEvent(event);
        setSelectedTicketType(matchedType || (types || [])[0] || null);
        setBookingForm({
          name: booking.name || "",
          email: booking.email || "",
          phone: booking.phone || "",
          quantity: booking.quantity || 1,
          has_pcd: !!booking.has_pcd,
          accessibility_needs: booking.accessibility_needs || "",
          name_individually: !!booking.name_individually,
          attendees: booking.attendees || [],
        });
        setCouponCode(booking.coupon_code || "");
        setCouponResult(null);
        setCreditsToApply(Number(booking.credits_applied || 0));
        setPendingBookingId(booking.id);
        setPendingBookingTotal(booking.total);
        setPaymentStep(true);
        setPaymentMethod(null);
        setPaymentResult(null);
        setPixData(null);
        setPixPolling(false);
        setShowBookingDialog(true);
        toast.success("Compra recuperada. Escolha a forma de pagamento para concluir.");
      } catch (error) {
        const message = error.response?.status === 410
          ? "Este link de retomada expirou. Faça um novo pedido para continuar."
          : "Não foi possível retomar este checkout.";
        toast.error(message);
        navigate("/eventos", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAbandonedCheckout();
    return () => {
      cancelled = true;
    };
  }, [fetchTicketTypes, navigate, resumeToken]);
  
  // Handle browser back button - close modal
  useEffect(() => {
    const handlePopState = () => {
      // When back button is pressed, if modal is open, close it
      if (showEventDetails) {
        setShowEventDetails(false);
        setDetailsEvent(null);
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showEventDetails]);

  useEffect(() => {
    if (selectedTicketType && bookingForm.quantity) {
      calculatePricing();
    }
  }, [selectedTicketType, bookingForm.quantity, totalCreditsToApply, couponResult, user, calculatePricing]);

  useEffect(() => {
    if (!selectedTicketType) return;

    const minQty = selectedTicketType.min_quantity || 1;
    const maxQty = selectedTicketType.max_quantity;
    const currentQty = parseInt(bookingForm.quantity);

    if (!currentQty || currentQty < minQty) {
      setBookingForm((prev) => ({ ...prev, quantity: minQty }));
      return;
    }

    if (maxQty && currentQty > maxQty) {
      setBookingForm((prev) => ({ ...prev, quantity: maxQty }));
    }
  }, [bookingForm.quantity, selectedTicketType]);

  // Clamp credits when quantity changes
  useEffect(() => {
    if (creditsToApply > 0 && creditsToApply > maxCredits) {
      setCreditsToApply(Math.max(0, maxCredits));
    }
  }, [bookingForm.quantity, maxCredits, creditsToApply]);

  useEffect(() => {
    const capacity = Math.max(0, (parseInt(bookingForm.quantity) || 1) - creditsToApply);
    let remaining = capacity;
    let changed = false;
    const next = {};
    for (const option of teamCreditOptions) {
      const current = Math.max(0, Number(teamCreditUses[option.user_id] || 0));
      const allowed = Math.min(current, Number(option.available_credits || 0), remaining);
      next[option.user_id] = allowed;
      remaining -= allowed;
      if (allowed !== current) changed = true;
    }
    if (changed) setTeamCreditUses(next);
  }, [bookingForm.quantity, creditsToApply, teamCreditOptions, teamCreditUses]);

  const filterEventsBySearch = (items = []) => {
    const query = eventSearch.trim().toLowerCase();
    if (!query) return items;

    return items.filter((event) => {
      const searchableFields = [
        event.title,
        event.description,
        event.location,
        event?.venue?.name,
        event.quiz_type,
        event.quiz_category,
        event.theme,
        event.themed_round_theme,
        event.event_type,
      ];

      return searchableFields
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  };

  const toggleCuradoriaFilter = (curadoria) => {
    resetVisibleEvents();
    setSelectedCuradorias((prev) =>
      prev.includes(curadoria)
        ? prev.filter((item) => item !== curadoria)
        : [...prev, curadoria]
    );
  };

  const togglePastEvents = () => {
    resetVisibleEvents();
    setShowPastEvents((previous) => !previous);
  };

  const handleEventSearchChange = (value) => {
    resetVisibleEvents();
    setEventSearch(value);
  };

  const filterEventsByCuradoria = (items = []) =>
    !selectedCuradorias.length
      ? items
      : items.filter((event) => {
      const matchedCuradoria = resolveCuradoriaForEvent(curadorias, event);
      if (!matchedCuradoria) return true;
      return selectedCuradorias.includes(matchedCuradoria.title);
    });

  const isPresentialEventBookable = (event) => {
    if (!event || event.event_type === "online" || event.is_past || isEventCancelled(event)) return false;
    const hasInventory = event.tickets_available == null || Number(event.tickets_available) > 0;
    if (!hasInventory) return false;
    if (event.requires_ticket) {
      return event.min_ticket_price !== null;
    }
    return true;
  };

  const isEventSoldOut = (event) => {
    if (!event || event.event_type === "online" || event.is_past || isEventCancelled(event)) return false;
    return !isPresentialEventBookable(event);
  };

  const isEventLastTickets = (event) => {
    if (!isPresentialEventBookable(event) || isEventSoldOut(event)) return false;
    if (event.tickets_available === null || event.tickets_available === undefined) return false;

    const remaining = Number(event.tickets_available);
    const sold = Number(event.tickets_sold || 0);
    const capacity = remaining + sold;
    return Number.isFinite(remaining)
      && Number.isFinite(sold)
      && remaining > 0
      && sold >= 0
      && capacity > 0
      && remaining / capacity <= 0.15;
  };

  const loadOwnedOnlineEventIds = useCallback(async () => {
    if (!user) {
      setOwnedOnlineEventIds({});
      return;
    }
    try {
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/bookings/my-tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ownedMap = {};
      (response.data || []).forEach((booking) => {
        if (booking?.event?.event_type === "online" && booking?.event_id) {
          ownedMap[booking.event_id] = true;
        }
      });
      setOwnedOnlineEventIds(ownedMap);
    } catch (error) {
      console.error("Error loading owned online tickets:", error);
      setOwnedOnlineEventIds({});
    }
  }, [user]);

  useEffect(() => {
    loadOwnedOnlineEventIds();
  }, [loadOwnedOnlineEventIds]);

  const eventNeedsTicket = useCallback((event) => {
    if (!event) return false;
    if (event.ticket_sales_paused) return false;
    return Boolean(
      event.requires_ticket
      || (event.selected_ticket_type_ids || []).length
      || (event.ticket_tiers || []).length
    );
  }, []);

  const userCanAccessOnlineEvent = useCallback((event) => {
    if (!event || event.event_type !== "online" || isEventCancelled(event)) return false;
    if (!eventNeedsTicket(event)) return true;
    if (!user) return false;
    const userPlan = user.subscription_plan || "free";
    if ((event.free_plans || []).includes(userPlan)) return true;
    return Boolean(ownedOnlineEventIds[event.id]);
  }, [eventNeedsTicket, ownedOnlineEventIds, user]);

  const isEmbeddedTicketedOnlineEvent = useCallback((event) => (
    event?.event_type === "online"
      && !isEventCancelled(event)
      && event?.is_streaming
      && event?.streaming_mode === "embedded_room"
      && eventNeedsTicket(event)
  ), [eventNeedsTicket]);

  const goToOnlineEventAsSpectator = useCallback((event) => {
    if (isEventCancelled(event)) {
      toast.error("Este evento foi cancelado.");
      return;
    }
    navigate(event.is_streaming && event.streaming_mode === "embedded_room" ? `/sala-evento/${event.id}` : `/quiz-especial/${event.id}`);
  }, [navigate]);

  const trackConfirmedPurchase = useCallback(async (bookingId) => {
    if (!bookingId || trackedBrowserPurchasesRef.current.has(bookingId)) return;
    trackedBrowserPurchasesRef.current.add(bookingId);

    try {
      const response = await axios.get(`${API}/bookings/checkout/status/${bookingId}`);
      const tracking = response.data?.meta_tracking;
      if (!tracking?.event_id) return;

      const { event_id: eventId, order_id: orderId, ...parameters } = tracking;
      trackMetaEvent("Purchase", { ...parameters, order_id: orderId }, eventId);
    } catch (error) {
      // The server-side CAPI event is authoritative if browser tracking is unavailable.
      console.warn("Não foi possível registrar a compra no Meta Pixel.", error);
    }
  }, []);

  const finishCheckoutSuccess = useCallback((message) => {
    setPixPolling(false);
    setPixData(null);
    setPaymentResult({ success: true, message: message || "Pagamento confirmado!" });
    toast.success(message || "Pagamento confirmado!");

    if (bookingReturnTo) {
      window.setTimeout(() => {
        navigate(bookingReturnTo, { replace: true });
      }, 400);
      return;
    }

    window.setTimeout(() => {
      const params = new URLSearchParams(location.search);
      if (params.has("booking")) {
        params.delete("booking");
        params.delete("return_to");
        navigate(
          {
            pathname: location.pathname,
            search: params.toString() ? `?${params.toString()}` : "",
          },
          { replace: true }
        );
      }
      setShowBookingDialog(false);
      setPaymentStep(false);
      setPaymentMethod(null);
      setPendingBookingId(null);
      setPendingBookingTotal(null);
      setPaymentResult(null);
      setPixData(null);
      setPixPolling(false);
      setCardForm({
        card_number: "",
        card_expiry: "",
        card_cvv: "",
        card_holder_name: "",
        cpf: "",
        postal_code: "",
        address_number: "",
        installments: 1,
      });
      fetchEvents();
      loadOwnedOnlineEventIds();
    }, 3000);
  }, [bookingReturnTo, fetchEvents, loadOwnedOnlineEventIds, location.pathname, location.search, navigate]);

  useCheckoutRealtime({
    checkoutKey: pendingBookingId,
    enabled: Boolean(pendingBookingId),
    onPending: () => {
      setPixPolling(true);
    },
    onConfirmed: (payload) => {
      trackConfirmedPurchase(pendingBookingId);
      finishCheckoutSuccess(payload?.meta?.message || "Pagamento confirmado!");
    },
    onExpired: (payload) => {
      setPixPolling(false);
      setPixData(null);
      const isCancelled = payload?.meta?.status === "cancelled";
      const message = isCancelled
        ? "O pagamento foi encerrado. Tente novamente."
        : "O pagamento expirou. Tente novamente.";
      setPaymentResult({ error: true, message });
      toast.error(message);
    },
  });

  useEffect(() => {
    if (!pixData?.payment_expires_at) {
      setPixTimeLeftMs(null);
      return;
    }

    const updateCountdown = () => {
      const diff = new Date(pixData.payment_expires_at).getTime() - Date.now();
      setPixTimeLeftMs(Math.max(0, diff));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);

    return () => window.clearInterval(intervalId);
  }, [pixData?.payment_expires_at]);

  useEffect(() => {
    if (!pixData || pixTimeLeftMs === null || pixTimeLeftMs > 0) return;

    setPixPolling(false);
    setPixData(null);
    setPaymentResult((current) => current?.error ? current : {
      error: true,
      message: "O PIX expirou após 1 hora. Gere um novo código para continuar.",
    });
  }, [pixData, pixTimeLeftMs]);

  // Handle event click - open modal and update URL
  const handleEventClick = useCallback((event) => {
    setDetailsEvent(event);
    setShowEventDetails(true);
    // Update URL to /eventos/:slug without adding to browser history stack for back button
    if (event.slug && window.location.pathname !== `/eventos/${event.slug}`) {
      navigate(`/eventos/${event.slug}${location.search}`, { replace: false });
    }
  }, [navigate, location.search]);
  
  // Handle modal close - navigate back to /eventos
  const handleCloseDetails = (open) => {
    if (!open) {
      setShowEventDetails(false);
      setDetailsEvent(null);
      // Update URL back to /eventos
      if (window.location.pathname !== '/eventos') {
        navigate(`/eventos${location.search}`, { replace: false });
      }
    } else {
      setShowEventDetails(true);
    }
  };
  
  const openShareEvent = (event) => {
    setShareEvent(event);
  };

  const canShowBuyButton = (event) => {
    if (!event) return false;
    if (event.event_type === "online") return false;
    return isPresentialEventBookable(event);
  };

  const renderOnlinePrimaryAction = useCallback((event, { compact = false, closeDetails = false } = {}) => {
    if (isEventCancelled(event)) return null;
    const hasPlayerAccess = userCanAccessOnlineEvent(event);
    const isEmbeddedTicketed = isEmbeddedTicketedOnlineEvent(event);
    const requiresOnlineTicket = eventNeedsTicket(event);
    const actionClasses = compact ? "rounded-lg px-4 py-2 text-sm" : "rounded-lg px-6 py-2";

    if (isEmbeddedTicketed && !hasPlayerAccess) {
      return (
        <div className="flex w-full flex-col gap-2">
          <Button
            onClick={() => {
              if (closeDetails) setShowEventDetails(false);
              handleOpenBooking(event);
            }}
            className={`bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c] ${actionClasses}`}
            data-testid={`buy-online-ticket-btn-${event.id}`}
          >
            Comprar
          </Button>
          <Button
            onClick={() => {
              if (closeDetails) setShowEventDetails(false);
              goToOnlineEventAsSpectator(event);
            }}
            variant="outline"
            className={`border-purple-500/40 text-purple-200 hover:bg-purple-500/10 ${actionClasses}`}
            data-testid={`spectator-online-btn-${event.id}`}
          >
            Entrar como Expectador
          </Button>
        </div>
      );
    }

    const label = event.is_streaming && event.streaming_mode === "embedded_room"
      ? "Entrar"
      : requiresOnlineTicket
        ? (hasPlayerAccess ? "Entrar" : "Ver")
        : "Jogar";

    return (
      <Button
        onClick={() => {
          if (closeDetails) setShowEventDetails(false);
          if (requiresOnlineTicket && !hasPlayerAccess && !(event.is_streaming && event.streaming_mode === "embedded_room")) {
            handleEventClick(event);
            return;
          }
          goToOnlineEventAsSpectator(event);
        }}
        className={`bg-purple-600 text-white font-nunito font-bold uppercase hover:bg-purple-700 ${compact ? "hover:scale-105 transition-all" : ""} ${actionClasses}`}
        data-testid={`play-quiz-btn-${event.id}`}
      >
        <Gamepad2 className="w-4 h-4 mr-1" />
        {label}
      </Button>
    );
  }, [eventNeedsTicket, goToOnlineEventAsSpectator, handleEventClick, handleOpenBooking, isEmbeddedTicketedOnlineEvent, userCanAccessOnlineEvent]);

  const getCuradoriaMeta = (event) => resolveCuradoriaForEvent(curadorias, event);
  const getCuradoriaLabel = (event) => getCuradoriaMeta(event)?.title || event?.quiz_type || "";
  const sourceEvents = showPastEvents
    ? (allEvents.past || [])
    : (allEvents.future || events || []);
  const displayEvents = filterEventsBySearch(filterEventsByCuradoria(sourceEvents));
  const visibleEvents = displayEvents.slice(0, visibleEventsCount);
  const hasMoreEvents = visibleEventsCount < displayEvents.length;

  const loadMoreEvents = useCallback(() => {
    if (!hasMoreEvents || isLoadingMoreEvents) return;

    setIsLoadingMoreEvents(true);
    loadMoreTimerRef.current = window.setTimeout(() => {
      setVisibleEventsCount((current) => Math.min(current + EVENTS_BATCH_SIZE, displayEvents.length));
      setIsLoadingMoreEvents(false);
      loadMoreTimerRef.current = null;
    }, 180);
  }, [displayEvents.length, hasMoreEvents, isLoadingMoreEvents]);

  useEffect(() => {
    if (!hasMoreEvents) return undefined;

    const handleScroll = () => {
      const documentHeight = document.documentElement.scrollHeight;
      const distanceFromBottom = documentHeight - window.innerHeight - window.scrollY;
      if (distanceFromBottom <= 280) loadMoreEvents();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMoreEvents, loadMoreEvents]);

  useEffect(() => () => {
    if (loadMoreTimerRef.current) window.clearTimeout(loadMoreTimerRef.current);
  }, []);

  // Open edit modal for an event
  const handleOpenEdit = async (event) => {
    setEditingEvent(event);
    setShowEditEvent(true);
  };

  // Save edited event
  const handleSaveEdit = async () => {
    if (!editingEvent || !editForm) return;
    setSavingEdit(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.put(`${API}/events/${editingEvent.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Evento atualizado com sucesso!");
      setShowEditEvent(false);
      setEditingEvent(null);
      setEditForm(null);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar evento");
    } finally {
      setSavingEdit(false);
    }
  };

  // Handle quantity change - update attendees array if naming individually
  const handleQuantityChange = (newQuantity) => {
    // Allow empty string for typing
    if (newQuantity === "" || newQuantity === null) {
      setBookingForm(prev => ({ ...prev, quantity: "" }));
      return;
    }
    
    const qty = parseInt(newQuantity);
    
    // Validate the quantity
    if (isNaN(qty) || qty < 0) {
      return;
    }
    
    setBookingForm(prev => {
      const newAttendees = [...(prev.attendees || [])];
      // Adjust attendees array size to match quantity
      while (newAttendees.length < qty) {
        newAttendees.push({ name: "", email: "" });
      }
      while (newAttendees.length > qty) {
        newAttendees.pop();
      }
      return { ...prev, quantity: qty, attendees: newAttendees };
    });
  };

  // Validate quantity on blur (when user leaves the field)
  const validateQuantityOnBlur = () => {
    const qty = parseInt(bookingForm.quantity);
    const minQty = selectedTicketType?.min_quantity || 1;
    const maxQty = selectedTicketType?.max_quantity || selectedEvent?.tickets_available || 100;
    
    if (isNaN(qty) || qty < minQty) {
      toast.warning(`Quantidade mínima: ${minQty} ingresso(s). Ajustando automaticamente.`);
      handleQuantityChange(minQty);
      return;
    }
    
    if (maxQty && qty > maxQty) {
      toast.warning(`Quantidade máxima: ${maxQty} ingresso(s). Ajustando automaticamente.`);
      handleQuantityChange(maxQty);
      return;
    }
  };

  // Get quantity validation error message
  const getQuantityError = () => {
    const qty = parseInt(bookingForm.quantity);
    if (bookingForm.quantity === "" || bookingForm.quantity === null) return null;
    
    const minQty = selectedTicketType?.min_quantity || 1;
    const maxQty = selectedTicketType?.max_quantity || selectedEvent?.tickets_available;
    
    if (isNaN(qty) || qty < minQty) {
      return `Mínimo ${minQty} ingresso(s)`;
    }
    if (maxQty && qty > maxQty) {
      return `Máximo ${maxQty} ingresso(s)`;
    }
    return null;
  };

  // Handle toggling name individually
  const handleNameIndividuallyChange = (checked) => {
    setBookingForm(prev => {
      if (checked) {
        // Initialize attendees array with empty entries
        const qty = prev.quantity;
        const attendees = Array.from({ length: qty }, () => ({ name: "", email: "" }));
        return { ...prev, name_individually: true, attendees };
      } else {
        return { ...prev, name_individually: false, attendees: [] };
      }
    });
  };

  // Update specific attendee
  const handleAttendeeChange = (index, field, value) => {
    setBookingForm(prev => {
      const newAttendees = [...prev.attendees];
      newAttendees[index] = { ...newAttendees[index], [field]: value };
      return { ...prev, attendees: newAttendees };
    });
  };

  const validateCoupon = async () => {
    if (!couponCode.trim() || !pricing) return;
    setValidatingCoupon(true);
    try {
      const res = await axios.get(`${API}/subscription/coupons/validate/${couponCode}?order_type=tickets&amount=${pricing.paidSubtotal}`);
      setCouponResult(res.data);
      toast.success(`Cupom "${couponCode}" aplicado!`);
    } catch (err) {
      setCouponResult(null);
      toast.error(err.response?.data?.detail || "Cupom inválido");
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode("");
    setCouponResult(null);
  };

  const validateQuantity = () => {
    if (!selectedTicketType) return true;

    const qty = parseInt(bookingForm.quantity);
    const { min_quantity, max_quantity } = selectedTicketType;

    if (min_quantity && qty < min_quantity) {
      toast.error(`Quantidade mínima: ${min_quantity} ingressos`);
      return false;
    }

    if (max_quantity && qty > max_quantity) {
      toast.error(`Quantidade máxima: ${max_quantity} ingressos`);
      return false;
    }

    return true;
  };

  const validateAttendees = () => {
    if (!bookingForm.name_individually) return true;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    for (let i = 0; i < bookingForm.attendees.length; i++) {
      const att = bookingForm.attendees[i];
      if (!att.name || !att.name.trim()) {
        toast.error(`Preencha o nome do convidado ${i + 1}`);
        return false;
      }
      if (!att.email || !emailRegex.test(att.email)) {
        toast.error(`E-mail inválido para o convidado ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const handleBooking = async () => {
    const isOnlineIndividualPurchase = isOnlineGameEvent(selectedEvent);

    if (isOnlineIndividualPurchase && !user) {
      toast.error("Faça login para comprar o ingresso individual deste evento online.");
      return;
    }
    if (!bookingForm.name || !bookingForm.email) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    // Phone is required and must have 11 digits
    const phoneDigits = (bookingForm.phone || "").replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      toast.error("Preencha o número de celular completo");
      return;
    }

    if (!isValidEmail(bookingForm.email)) {
      toast.error("Digite um e-mail válido");
      return;
    }

    if (!selectedTicketType) {
      toast.error("Selecione um tipo de ingresso");
      return;
    }

    if (!isValidPhoneBR(bookingForm.phone)) {
      toast.error("Digite um telefone válido com DDD");
      return;
    }

    if (!validateQuantity()) {
      return;
    }

    if (!validateAttendees()) {
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const bookingData = {
        event_id: selectedEvent.id,
        ticket_type: selectedTicketType.type,
        name: bookingForm.name,
        email: bookingForm.email,
        phone: bookingForm.phone || null,
        quantity: isOnlineIndividualPurchase ? 1 : bookingForm.quantity,
        // Accessibility requests apply only to in-person venues.
        has_pcd: !isOnlineGameEvent(selectedEvent) && !!bookingForm.has_pcd,
        accessibility_needs: !isOnlineGameEvent(selectedEvent) && bookingForm.has_pcd
          ? ((bookingForm.accessibility_needs || "").trim() || null)
          : null,
        name_individually: isOnlineIndividualPurchase ? false : bookingForm.name_individually,
        attendees: isOnlineIndividualPurchase ? null : (bookingForm.name_individually ? bookingForm.attendees : null),
        origin_url: window.location.origin,
        credits_to_apply: creditsToApply,
        team_credit_uses: Object.entries(teamCreditUses)
          .filter(([, amount]) => Number(amount) > 0)
          .map(([user_id, amount]) => ({ user_id, amount: Number(amount) })),
        coupon_code: couponResult ? couponResult.coupon.code : null,
      };

      const bookingResponse = await axios.post(`${API}/bookings`, bookingData, { headers });

      const actualTotal = bookingResponse.data.total;

      if (actualTotal === 0) {
        // Free booking - confirm with credits or coupon
        const res = await axios.post(
          `${API}/bookings/${bookingResponse.data.id}/confirm-free`,
          {},
          { headers }
        );
        setPendingBookingId(bookingResponse.data.id);
        setPaymentMethod("ticket_credit");
        finishCheckoutSuccess(res.data.message || "Compra aprovada! Seus ingressos foram gerados.");
        return;
      }

      if (bookingResponse.data.payment_provider === "infinitepay") {
        const checkout = await axios.post(
          `${API}/bookings/${bookingResponse.data.id}/checkout`,
          { origin_url: window.location.origin },
          { headers }
        );
        window.location.assign(checkout.data.url);
        return;
      }

      // Show transparent checkout payment form
      setPendingBookingId(bookingResponse.data.id);
      setPendingBookingTotal(actualTotal);
      setPaymentStep(true);
      setPaymentResult(null);
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(getApiErrorMessage(error, "Erro ao processar reserva"));
    } finally {
      setLoading(false);
    }
  };

  const choosePaymentMethod = (method) => {
    setPaymentMethod(method);
    if (!pendingBookingId || trackedPaymentMethodsRef.current.has(`${pendingBookingId}:${method}`)) return;
    trackedPaymentMethodsRef.current.add(`${pendingBookingId}:${method}`);
    trackMetaEvent(
      "AddPaymentInfo",
      buildMetaCheckoutPayload(selectedEvent, {
        value: pendingBookingTotal ?? pricing?.total ?? 0,
        quantity: bookingForm.quantity,
      }),
      `${pendingBookingId}:payment:${method}`,
    );
  };

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
  };

  // Format card expiry MM/YY
  const formatExpiry = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  };

  // Format CEP: 00000-000
  const formatCEP = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  // Handle card payment submission
  const handleCardPayment = async () => {
    if (!pendingBookingId) return;

    // Validate card fields
    const cardDigits = cardForm.card_number.replace(/\D/g, "");
    if (cardDigits.length < 13) return toast.error("Número do cartão inválido");
    if (cardForm.card_expiry.length < 5) return toast.error("Validade inválida (MM/AA)");
    if (cardForm.card_cvv.length < 3) return toast.error("CVV inválido");
    if (!cardForm.card_holder_name.trim()) return toast.error("Nome do titular obrigatório");
    const cpfDigits = cardForm.cpf.replace(/\D/g, "");
    if (!isValidCPF(cardForm.cpf)) return toast.error("Digite um CPF válido");
    const cepDigits = cardForm.postal_code.replace(/\D/g, "");
    if (cepDigits.length !== 8) return toast.error("CEP inválido");
    if (!cardForm.address_number.trim()) return toast.error("Número do endereço obrigatório");

    const [expiryMonth, expiryYear] = cardForm.card_expiry.split("/");

    setLoading(true);
    setPaymentResult(null);
    try {
      const res = await axios.post(`${API}/bookings/${pendingBookingId}/pay-card`, {
        card_number: cardDigits,
        card_expiry_month: expiryMonth,
        card_expiry_year: expiryYear.length === 2 ? `20${expiryYear}` : expiryYear,
        card_cvv: cardForm.card_cvv,
        card_holder_name: cardForm.card_holder_name.toUpperCase(),
        cpf: cpfDigits,
        postal_code: cepDigits,
        address_number: cardForm.address_number,
        installments: cardForm.installments,
      });

      if (res.data.status === "confirmed" || res.data.status === "already_confirmed") {
        trackConfirmedPurchase(pendingBookingId);
        finishCheckoutSuccess(res.data.message || "Pagamento aprovado!");
      } else if (res.data.status === "pending") {
        setPaymentResult({ pending: true, message: res.data.message });
      } else {
        setPaymentResult({ error: true, message: res.data.message || "Pagamento não aprovado" });
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao processar pagamento";
      setPaymentResult({ error: true, message: msg });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Handle PIX payment
  const handlePixPayment = async () => {
    if (!pendingBookingId) return;
    const cpfDigits = cardForm.cpf.replace(/\D/g, "");
    if (!isValidCPF(cardForm.cpf)) return toast.error("Digite um CPF válido para gerar o PIX");
    const cepDigits = cardForm.postal_code.replace(/\D/g, "");
    if (cepDigits.length !== 8) return toast.error("Digite um CEP válido para gerar o PIX");
    if (!cardForm.address_number.trim()) return toast.error("Digite o número do endereço para gerar o PIX");

    setLoading(true);
    setPaymentResult(null);
    try {
      const res = await axios.post(`${API}/bookings/${pendingBookingId}/pay-pix`, {
        cpf: cpfDigits,
        postal_code: cepDigits,
        address_number: cardForm.address_number.trim(),
      });

      if (res.data.status === "awaiting_payment") {
        setPixData({
          qr_code_image: res.data.qr_code_image,
          qr_code_payload: res.data.qr_code_payload,
          payment_id: res.data.payment_id,
          payment_expires_at: res.data.payment_expires_at,
        });
        setPixPolling(true);
      } else if (res.data.status === "already_confirmed") {
        finishCheckoutSuccess(res.data.message || "Pagamento confirmado!");
      }
    } catch (error) {
      const msg = getApiErrorMessage(error, "Erro ao gerar PIX");
      toast.error(msg);
      setPaymentResult({ error: true, message: msg });
    } finally {
      setLoading(false);
    }
  };

  // Copy PIX code to clipboard
  const copyPixCode = () => {
    if (pixData?.qr_code_payload) {
      navigator.clipboard.writeText(pixData.qr_code_payload);
      toast.success("Código PIX copiado!");
    }
  };

  // Reset payment step when dialog closes
  const handleBookingDialogClose = (open) => {
    setShowBookingDialog(open);
    if (!open) {
      if (resumeToken) {
        navigate("/eventos", { replace: true });
      } else if (bookingReturnTo) {
        navigate(bookingReturnTo, { replace: true });
      } else {
        const params = new URLSearchParams(location.search);
        if (params.has("booking")) {
          params.delete("booking");
          params.delete("return_to");
          navigate(
            {
              pathname: location.pathname,
              search: params.toString() ? `?${params.toString()}` : "",
            },
            { replace: true }
          );
        }
      }
      setPaymentStep(false);
      setPaymentMethod(null);
      setPendingBookingId(null);
      setPendingBookingTotal(null);
      setPaymentResult(null);
      setPixData(null);
      setPixPolling(false);
      setCardForm({ card_number: "", card_expiry: "", card_cvv: "", card_holder_name: "", cpf: "", postal_code: "", address_number: "", installments: 1 });
    }
  };

  const selectedVenueAccessibilityInfo = selectedEvent?.venue?.accessibility_info || "";

  // SEO: determine what to show
  const seoProps = detailsEvent ? {
    title: `${detailsEvent.title} | Lord of the Quiz`,
    description: buildEventSeoDescription(detailsEvent),
    image: detailsEvent.image_url,
    url: `${SITE_URL}/eventos/${detailsEvent.slug}`,
    event: detailsEvent,
    pathname: `/eventos/${detailsEvent.slug}`
  } : {
    pathname: '/eventos'
  };

  const appEventsContent = (
    <AppEventsPage
      curadorias={curadorias}
      displayEvents={displayEvents}
      eventSearch={eventSearch}
      getCuradoriaLabel={getCuradoriaLabel}
      getCuradoriaMeta={getCuradoriaMeta}
      handleEventClick={handleEventClick}
      handleOpenBooking={handleOpenBooking}
      handleOpenEdit={handleOpenEdit}
      isAdmin={isAdmin}
      isEventLastTickets={isEventLastTickets}
      isEventSoldOut={isEventSoldOut}
      isPresentialEventBookable={isPresentialEventBookable}
      navigate={navigate}
      visibleEvents={visibleEvents}
      renderOnlinePrimaryAction={renderOnlinePrimaryAction}
      selectedCuradorias={selectedCuradorias}
      showPastEvents={showPastEvents}
      setEventSearch={handleEventSearchChange}
      togglePastEvents={togglePastEvents}
      toggleCuradoriaFilter={toggleCuradoriaFilter}
      hasMoreEvents={hasMoreEvents}
      isLoadingMoreEvents={isLoadingMoreEvents}
    />
  );

  return (
    <div className="min-h-screen" data-testid="events-page">
      {isApp ? appEventsContent : (
        <>
      <SEO {...seoProps} />
      {/* CMS Hero Section or Default Header */}
      {normalizedHeroSection ? (
        <SectionRenderer section={normalizedHeroSection} />
      ) : (
        <div className="pt-32 pb-8">
          <div className="container mx-auto px-4 md:px-6 max-w-7xl">
            <div className="text-center">
              <h1
                className="font-nunito font-black text-5xl md:text-7xl tracking-tight text-[#fccf1f] uppercase mb-4"
                style={{ textShadow: "0 0 15px rgba(110,70,214,0.18)" }}
                data-testid="events-title"
              >
                Eventos
              </h1>
              <p className="font-outfit text-lg md:text-xl text-zinc-300 max-w-2xl mx-auto">
                Compre seus ingressos e garanta sua vaga nos nossos Quiz Shows!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Events Grid */}
      <div className="pt-4 pb-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Admin: Create Event */}
          {isAdmin && (
            <div className="flex justify-end mb-6">
              <Button
                onClick={() => navigate("/admin")}
                className="bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c]"
                data-testid="create-event-btn"
              >
                <PlusCircle className="w-4 h-4 mr-2" /> Criar Evento
              </Button>
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {getCuradoriaTitles(curadorias).map((curadoria) => {
              const active = selectedCuradorias.includes(curadoria);
              return (
                <Button
                  key={curadoria}
                  type="button"
                  onClick={() => toggleCuradoriaFilter(curadoria)}
                  variant={active ? "default" : "outline"}
                  className={active ? "bg-[#fccf1f] text-black hover:bg-[#e6bc1c]" : "border-zinc-600 text-zinc-300"}
                  data-testid={`events-curadoria-filter-${curadoria.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {curadoria}
                </Button>
              );
            })}
            <Button
              type="button"
              onClick={togglePastEvents}
              variant={showPastEvents ? "default" : "outline"}
              className={showPastEvents ? "bg-[#fccf1f] text-black hover:bg-[#e6bc1c]" : "border-zinc-600 text-zinc-300"}
              data-testid="events-past-filter"
            >
              Eventos Passados
            </Button>
          </div>

          <div className="mb-8 max-w-xl mx-auto">
            <div className="relative">
              <Search className="w-4 h-4 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <Input
                value={eventSearch}
                onChange={(e) => handleEventSearchChange(e.target.value)}
                placeholder="Buscar eventos por nome, tema, tipo ou local"
                className="pl-10 bg-zinc-900/60 border-white/10 text-white placeholder:text-zinc-500"
                data-testid="events-search-input"
              />
            </div>
          </div>

          {/* Events to display */}
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {visibleEvents.map((event) => (
            <div
              key={event.id}
              className={`bg-zinc-900/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 group ${event.is_past || isEventCancelled(event) ? "opacity-80" : "hover:border-[#fccf1f]/50"}`}
              data-testid={`event-card-${event.id}`}
            >
              {event.image_url && (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={event.image_url}
                    alt={event.title}
                    className={`w-full h-full object-cover transition-transform duration-300 ${event.is_past || isEventCancelled(event) ? "grayscale" : "group-hover:scale-110"}`}
                  />
                  {isEventCancelled(event) ? (
                    <div className="absolute left-4 top-4 z-10">
                      <span className="inline-flex items-center rounded-full bg-red-600/95 px-3 py-1 text-xs font-black tracking-[0.18em] text-white shadow-lg shadow-red-950/40">
                        CANCELADO
                      </span>
                    </div>
                  ) : isEventSoldOut(event) && (
                    <div className="absolute left-4 top-4 z-10">
                      <span className="inline-flex items-center rounded-full bg-red-600/95 px-3 py-1 text-xs font-black tracking-[0.18em] text-white shadow-lg shadow-red-950/40">
                        ESGOTADO
                      </span>
                    </div>
                  )}
                  {!isEventCancelled(event) && !isEventSoldOut(event) && isEventLastTickets(event) && (
                    <div className="absolute left-4 top-4 z-10">
                      <span className="inline-flex items-center rounded-full bg-red-600 px-3 py-1 text-xs font-black tracking-[0.18em] text-white shadow-lg shadow-red-950/40">
                        ÚLTIMOS INGRESSOS!
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className="p-6">
                {/* Event Type Badge */}
                <div className="flex items-center gap-2 mb-2">
                  {event.event_type === "online" ? (
                    <span className="inline-flex items-center gap-1 bg-purple-500/20 text-purple-400 text-xs font-bold px-2 py-1 rounded-full">
                      <Gamepad2 className="w-3 h-3" /> QUIZ ONLINE
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 bg-green-500/20 text-green-400 text-xs font-bold px-2 py-1 rounded-full">
                      <MapPin className="w-3 h-3" /> PRESENCIAL
                    </span>
                  )}
                  {event.prize_description && (
                    <span className="inline-flex items-center gap-1 bg-[#fccf1f]/20 text-[#fccf1f] text-xs font-bold px-2 py-1 rounded-full">
                      <Trophy className="w-3 h-3" /> PREMIADO
                    </span>
                  )}
                  {isEventCancelled(event) ? (
                    <span className="inline-flex items-center gap-1 bg-red-600/90 text-white text-xs font-bold px-2 py-1 rounded-full">
                      CANCELADO
                    </span>
                  ) : event.is_past && (
                    <span className="inline-flex items-center gap-1 bg-zinc-700/70 text-zinc-200 text-xs font-bold px-2 py-1 rounded-full">
                      ENCERRADO
                    </span>
                  )}
                  {getCuradoriaMeta(event) && (
                    <span
                      className="lotq-curation-badge inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border"
                      style={{
                        color: getCuradoriaVisuals(getCuradoriaMeta(event)).color,
                        backgroundColor: getCuradoriaVisuals(getCuradoriaMeta(event)).badgeBg,
                        borderColor: getCuradoriaVisuals(getCuradoriaMeta(event)).badgeBorder,
                      }}
                    >
                      <Tag className="w-3 h-3" />
                      {getCuradoriaLabel(event)}
                    </span>
                  )}
                </div>
                
                <h3 className="font-nunito font-bold text-2xl text-[#fccf1f] mb-3">
                  {event.title}
                </h3>

                <div className="space-y-2 mb-6">
                  <div className="flex items-center text-zinc-400">
                    <Calendar size={18} className="mr-2" />
                    <span className="text-sm">{formatDateBR(event.date)}</span>
                  </div>
                  {event.event_type === "presencial" && event.time && (
                    <div className="flex items-center text-zinc-400">
                      <Clock size={18} className="mr-2" />
                      <span className="text-sm">{event.time}</span>
                    </div>
                  )}
                  {event.event_type === "presencial" && (event.venue || event.location) && (
                    <div className="flex items-start text-zinc-400">
                      <MapPin size={18} className="mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">
                        {event.venue?.name || event.location || "Local a confirmar"}
                        {(event.venue?.city || event.city) && <><br />{event.venue?.city || event.city}</>}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col">
                    {event.min_ticket_price > 0 ? (
                      <>
                        <span className="font-outfit text-xs text-zinc-500">a partir de</span>
                        <span className="font-nunito font-bold text-lg text-[#fccf1f]" data-testid={`event-price-${event.id}`}>
                          R$ {formatCurrencyBR(event.min_ticket_price)}
                        </span>
                      </>
                    ) : event.min_ticket_price === null ? (
                      null
                    ) : event.event_type === "online" && !event.requires_ticket ? (
                      <span className="font-outfit text-sm text-green-400">Gratuito</span>
                    ) : null}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEventClick(event)}
                      variant="outline"
                      className="border-zinc-600 text-zinc-300 hover:bg-zinc-800 text-sm px-3"
                      data-testid={`event-details-btn-${event.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" /> Info
                    </Button>
                    {isAdmin && (
                      <Button
                        onClick={() => handleOpenEdit(event)}
                        variant="outline"
                        className="border-orange-600/50 text-orange-400 hover:bg-orange-600/10 text-sm px-3"
                        data-testid={`event-edit-btn-${event.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-1" /> Editar
                      </Button>
                    )}
                    {event.event_type === "online" ? (
                      renderOnlinePrimaryAction(event, { compact: true })
                    ) : isPresentialEventBookable(event) ? (
                    <Button
                      onClick={() => handleOpenBooking(event)}
                      className="bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c] hover:scale-105 transition-all rounded-lg px-4 py-2 text-sm"
                      data-testid={`buy-ticket-btn-${event.id}`}
                    >
                      Comprar
                    </Button>
                  ) : null}
                  </div>
                </div>
              </div>
            </div>
                  ))}
                </div>

                {/* Empty State */}
                {visibleEvents.length === 0 && (
                  <div className="text-center py-16">
                    <p className="font-outfit text-xl text-zinc-400">
                      {eventSearch.trim()
                        ? "Nenhum evento encontrado para essa busca."
                        : "Nenhum evento disponível no momento. Volte em breve!"}
                    </p>
                    {eventSearch.trim() && (
                      <div className="mt-6 flex flex-col items-center gap-3">
                        <p className="font-outfit text-sm text-zinc-500">
                          Não achou o tema que queria? Sugira um novo evento para a gente avaliar.
                        </p>
                        <Button
                          onClick={() => navigate("/temas")}
                          className="bg-purple-600 text-white hover:bg-purple-700"
                          data-testid="events-empty-suggest-theme-btn"
                        >
                          Sugerir temas para eventos
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {displayEvents.length > 0 && (
                  <div className="mt-12 flex min-h-12 items-center justify-center border-t border-white/10 pt-8 text-center">
                    {isLoadingMoreEvents ? (
                      <span className="inline-flex items-center gap-2 font-outfit text-sm text-zinc-400">
                        <Loader2 className="h-4 w-4 animate-spin text-[#fccf1f]" /> Carregando mais eventos...
                      </span>
                    ) : hasMoreEvents ? (
                      <span className="font-outfit text-xs uppercase tracking-[0.16em] text-zinc-600">Role para carregar mais eventos</span>
                    ) : (
                      <span className="font-outfit text-sm text-zinc-600">Todos os eventos foram carregados.</span>
                    )}
                  </div>
                )}
              </>
        </div>
      </div>

        </>
      )}

      {/* Event Details Dialog */}
      <Dialog open={showEventDetails} onOpenChange={handleCloseDetails}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-details-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="font-nunito font-bold text-2xl text-[#fccf1f]">
                {detailsEvent?.title}
              </DialogTitle>
              {isAdmin && detailsEvent && (
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setShowEventDetails(false); setShowEventSales(detailsEvent.id); }}
                    className="border-green-600/50 text-green-400 hover:bg-green-600/10"
                    data-testid="event-sales-btn"
                  >
                    <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Vendas
                  </Button>
                </div>
              )}
            </div>
            <DialogDescription className="text-zinc-400">Detalhes do evento</DialogDescription>
          </DialogHeader>
          {detailsEvent && (
            <div className="space-y-5">
              {detailsEvent.image_url && (
                <div className="relative h-48 rounded-lg overflow-hidden">
                  <img src={detailsEvent.image_url} alt={detailsEvent.title} className={`w-full h-full object-cover ${isEventCancelled(detailsEvent) ? "grayscale" : ""}`} />
                  {isEventCancelled(detailsEvent) ? (
                    <span className="absolute left-4 top-4 inline-flex rounded-full bg-red-600/95 px-3 py-1 text-xs font-black tracking-[0.18em] text-white shadow-lg shadow-red-950/40">
                      CANCELADO
                    </span>
                  ) : isEventSoldOut(detailsEvent) ? (
                    <span className="absolute left-4 top-4 inline-flex rounded-full bg-red-600/95 px-3 py-1 text-xs font-black tracking-[0.18em] text-white shadow-lg shadow-red-950/40">
                      ESGOTADO
                    </span>
                  ) : isEventLastTickets(detailsEvent) ? (
                    <span className="absolute left-4 top-4 inline-flex rounded-full bg-red-600 px-3 py-1 text-xs font-black tracking-[0.18em] text-white shadow-lg shadow-red-950/40">
                      ÚLTIMOS INGRESSOS!
                    </span>
                  ) : null}
                </div>
              )}

              {/* Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                {detailsEvent.event_type === "online" ? (
                  <span className="inline-flex items-center gap-1 bg-purple-500/20 text-purple-400 text-xs font-bold px-2 py-1 rounded-full"><Gamepad2 className="w-3 h-3" /> QUIZ ONLINE</span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-green-500/20 text-green-400 text-xs font-bold px-2 py-1 rounded-full"><MapPin className="w-3 h-3" /> PRESENCIAL</span>
                )}
                {detailsEvent.prize_description && (
                  <span className="inline-flex items-center gap-1 bg-[#fccf1f]/20 text-[#fccf1f] text-xs font-bold px-2 py-1 rounded-full"><Trophy className="w-3 h-3" /> PREMIADO</span>
                )}
                {isEventCancelled(detailsEvent) ? (
                  <span className="inline-flex items-center gap-1 bg-red-600/90 text-white text-xs font-bold px-2 py-1 rounded-full">CANCELADO</span>
                ) : null}
                {getCuradoriaMeta(detailsEvent) && (
                  <span
                    className="lotq-curation-badge inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border"
                    style={{
                      color: getCuradoriaVisuals(getCuradoriaMeta(detailsEvent)).color,
                      backgroundColor: getCuradoriaVisuals(getCuradoriaMeta(detailsEvent)).badgeBg,
                      borderColor: getCuradoriaVisuals(getCuradoriaMeta(detailsEvent)).badgeBorder,
                    }}
                  >
                    <Tag className="w-3 h-3" />
                    {getCuradoriaLabel(detailsEvent)}
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="font-outfit text-zinc-300">{detailsEvent.description}</p>

              {getCuradoriaMeta(detailsEvent)?.categories?.length ? (
                <div className="bg-zinc-800/40 border border-white/10 rounded-lg p-4 space-y-3">
                  <div className="space-y-1">
                    <p className="font-nunito font-bold text-white text-sm">
                      Essa curadoria também passa por outros temas
                    </p>
                    <p className="font-outfit text-zinc-400 text-sm">
                      O tema da semana é o destaque, mas o jogo também passeia pelos assuntos-base desta curadoria.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {getCuradoriaMeta(detailsEvent).categories.map((theme) => (
                      <span
                        key={theme}
                        className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold border"
                        style={{
                          color: getCuradoriaVisuals(getCuradoriaMeta(detailsEvent)).color,
                          backgroundColor: getCuradoriaVisuals(getCuradoriaMeta(detailsEvent)).badgeBg,
                          borderColor: getCuradoriaVisuals(getCuradoriaMeta(detailsEvent)).badgeBorder,
                        }}
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Details grid */}
              <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center text-zinc-300">
                  <Calendar size={18} className="mr-3 text-[#fccf1f]" />
                  <span className="text-sm">{formatDateBR(detailsEvent.date)}</span>
                </div>

                {detailsEvent.event_type === "online" ? (
                  <>
                    {detailsEvent.online_start_datetime && (
                      <div className="flex items-center text-zinc-300">
                        <Clock size={18} className="mr-3 text-[#fccf1f]" />
                        <span className="text-sm">Disponível: {new Date(detailsEvent.online_start_datetime).toLocaleString('pt-BR')} até {new Date(detailsEvent.online_end_datetime).toLocaleString('pt-BR')}</span>
                      </div>
                    )}
                    {!isEventCancelled(detailsEvent) && detailsEvent.is_streaming && detailsEvent.streaming_link && (
                      <div className="flex items-center text-zinc-300">
                        <ExternalLink size={18} className="mr-3 text-[#fccf1f]" />
                        {detailsEvent.streaming_mode === "embedded_room" ? (
                          <button onClick={() => { setShowEventDetails(false); navigate(`/sala-evento/${detailsEvent.id}`); }} className="text-sm text-[#fccf1f] hover:underline">
                            {detailsEvent.requires_ticket ? "Abrir sala e ingressos" : "Abrir sala incorporada"}
                          </button>
                        ) : (
                          <a href={detailsEvent.streaming_link} target="_blank" rel="noopener noreferrer" className="text-sm text-[#fccf1f] hover:underline">Assistir ao vivo</a>
                        )}
                      </div>
                    )}
                    <div className="flex items-center text-zinc-300">
                      <Users size={18} className="mr-3 text-[#fccf1f]" />
                      <span className="text-sm">Habilitados: {detailsEvent.eligible_plans?.map(p => p === 'free' ? 'Grátis' : p === 'pro' ? 'Pro' : 'Master').join(', ')}</span>
                    </div>
                  </>
                ) : (
                  <>
                    {detailsEvent.time && (
                      <div className="flex items-center text-zinc-300">
                        <Clock size={18} className="mr-3 text-[#fccf1f]" />
                        <span className="text-sm">{detailsEvent.time}{detailsEvent.end_time ? ` até ${detailsEvent.end_time}` : ''}</span>
                      </div>
                    )}
                    <div className="flex items-start text-zinc-300">
                      <MapPin size={18} className="mr-3 mt-0.5 text-[#fccf1f] flex-shrink-0" />
                      <div className="text-sm">
                        {detailsEvent.venue ? (
                          <>
                            {detailsEvent.venue.link ? (
                              <a href={detailsEvent.venue.link} target="_blank" rel="noopener noreferrer" className="text-[#fccf1f] hover:underline block">{detailsEvent.venue.name}</a>
                            ) : (
                              <span className="text-white">{detailsEvent.venue.name}</span>
                            )}
                            {detailsEvent.venue.address && (
                              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(detailsEvent.venue.address)}`} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-zinc-200 text-xs block mt-0.5">{detailsEvent.venue.address}</a>
                            )}
                          </>
                        ) : detailsEvent.location ? (
                          <span>{detailsEvent.location}</span>
                        ) : null}
                      </div>
                    </div>
                    {detailsEvent.show_capacity && (
                      <div className="flex items-center text-zinc-300">
                        <Ticket size={18} className="mr-3 text-[#fccf1f]" />
                        <span className="text-sm">{detailsEvent.tickets_available} ingressos disponíveis</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Prize */}
              {detailsEvent.prize_description && (
                <div className="bg-[#fccf1f]/10 border border-[#fccf1f]/30 rounded-lg p-4">
                  <p className="font-nunito font-bold text-[#fccf1f] mb-1 text-sm uppercase">Premiação</p>
                  <p className="font-outfit text-white">{detailsEvent.prize_description}</p>
                </div>
              )}

              {/* Share Button */}
              {detailsEvent.slug && (
                <Button
                  onClick={() => openShareEvent(detailsEvent)}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-nunito font-bold uppercase tracking-wide hover:from-purple-500 hover:to-pink-500 shadow-[0_0_20px_rgba(168,85,247,0.3)] transition-all duration-200 rounded-lg py-3"
                  data-testid="share-event-btn"
                >
                  <Share2 className="w-5 h-5 mr-2" /> Compartilhar Evento
                </Button>
              )}

              {/* CTA */}
              {!isEventCancelled(detailsEvent) ? (
              <div className="flex items-center justify-between pt-2">
                {detailsEvent.event_type === "online" ? (
                  <>
                    <div>
                      {detailsEvent.requires_ticket ? (
                        <>
                          <span className="font-outfit text-sm text-zinc-400">a partir de</span>
                          <p className="font-nunito font-bold text-2xl text-[#fccf1f]" data-testid="event-detail-price">R$ {formatCurrencyBR(detailsEvent.min_ticket_price || detailsEvent.ticket_tiers?.[0]?.price || 10)}</p>
                          {detailsEvent.free_plans?.length > 0 && (
                            <span className="font-outfit text-xs text-green-400">Grátis para: {detailsEvent.free_plans.map(p => p === 'free' ? 'Casual' : p === 'pro' ? 'Pro' : 'Supremo').join(', ')}</span>
                          )}
                        </>
                      ) : (
                        <span className="font-outfit text-sm text-green-400">Acesso Gratuito</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end">
                      {renderOnlinePrimaryAction(detailsEvent, { closeDetails: true })}
                    </div>
                  </>
                ) : (
                  <>
                    {detailsEvent.min_ticket_price != null && detailsEvent.min_ticket_price > 0 ? (
                      <div>
                        <span className="font-outfit text-sm text-zinc-400">a partir de</span>
                        <p className="font-nunito font-bold text-2xl text-[#fccf1f]" data-testid="event-detail-price">
                          R$ {formatCurrencyBR(detailsEvent.min_ticket_price)}
                        </p>
                        <span className="font-outfit text-xs text-zinc-500">+ taxas</span>
                      </div>
                    ) : detailsEvent.min_ticket_price === null ? (
                      <div>
                        <p className="font-outfit text-sm text-zinc-400" data-testid="event-detail-no-tickets">
                          Ingressos não configurados
                        </p>
                      </div>
                    ) : (
                      <div>
                        <span className="font-outfit text-sm text-zinc-400">a partir de</span>
                        <p className="font-nunito font-bold text-2xl text-[#fccf1f]" data-testid="event-detail-price">
                          R$ {formatCurrencyBR(detailsEvent.price || 0)}
                        </p>
                        <span className="font-outfit text-xs text-zinc-500">+ taxas</span>
                      </div>
                    )}
                    {canShowBuyButton(detailsEvent) ? (
                      <Button onClick={() => { setShowEventDetails(false); handleOpenBooking(detailsEvent); }} className="bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c] rounded-lg px-6 py-2">
                        Comprar Ingresso
                      </Button>
                    ) : null}
                  </>
                )}
              </div>
              ) : (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm font-semibold text-red-200">
                  Este evento foi cancelado.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ShareDialog
        open={!!shareEvent}
        onOpenChange={(open) => {
          if (!open) setShareEvent(null);
        }}
        title={shareEvent?.title || "Lord of the Quiz"}
        text={shareEvent ? `${shareEvent.title} - Lord of the Quiz` : ""}
        url={
          shareEvent?.slug
            ? `${SITE_URL}/eventos/${shareEvent.slug}`
            : `${SITE_URL}/eventos`
        }
        imageHint="Ao compartilhar este evento, a capa do próprio evento será usada nas prévias compatíveis."
      />

      {/* Booking Dialog */}
      <Dialog open={showBookingDialog} onOpenChange={handleBookingDialogClose}>
        <DialogContent className="bg-zinc-900 border-white/10 w-[calc(100vw-24px)] max-w-[calc(100vw-24px)] sm:max-w-2xl max-h-[90dvh] overflow-y-auto overflow-x-hidden" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-nunito font-bold text-2xl text-[#fccf1f]">
              {paymentResult?.success ? "Compra Aprovada" : paymentResult?.pending ? "Pagamento em Processamento" : paymentStep ? "Pagamento" : "Reservar Ingressos"}
            </DialogTitle>
          </DialogHeader>
          {selectedEvent && (
            <TooltipProvider delayDuration={120}>
            <div className="space-y-6">
              <div className="bg-zinc-800/50 p-4 rounded-lg">
                <p className="font-outfit text-white font-bold text-lg">
                  {selectedEvent.title}
                </p>
                <p className="font-outfit text-zinc-400 text-sm">
                  {formatDateBR(selectedEvent.date)} • {selectedEvent.time}
                </p>
                {paymentStep && pricing && (
                  <p className="font-nunito font-bold text-[#fccf1f] text-lg mt-2">Total: R$ {pricing.total}</p>
                )}
              </div>

              {/* Payment Result */}
              {paymentResult?.success && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6 text-center" data-testid="payment-success">
                  <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <p className="text-green-400 font-bold text-lg">{paymentResult.message}</p>
                  <p className="text-zinc-400 text-sm mt-2">Seus ingressos foram enviados para o seu e-mail.</p>
                </div>
              )}

              {paymentResult?.pending && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6 text-center" data-testid="payment-pending">
                  <Clock className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
                  <p className="text-yellow-400 font-bold text-lg">{paymentResult.message}</p>
                </div>
              )}

              {paymentResult?.error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center mb-2" data-testid="payment-error">
                  <p className="text-red-400 font-medium">{paymentResult.message}</p>
                </div>
              )}

              {/* Payment Step */}
              {paymentStep && !paymentResult?.success && !paymentResult?.pending && (
                <div className="space-y-4" data-testid="payment-step">
                  {/* Payment Method Selector (only show if no PIX QR yet) */}
                  {!pixData && !paymentMethod && (
                    <div className="space-y-3" data-testid="payment-method-selector">
                      <p className="text-zinc-400 text-sm">Escolha a forma de pagamento:</p>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => choosePaymentMethod("card")}
                          className="flex flex-col items-center gap-2 p-4 rounded-lg border border-white/10 bg-zinc-900/50 hover:bg-zinc-800 hover:border-[#fccf1f]/50 transition-all"
                          data-testid="select-card-btn"
                        >
                          <CreditCard className="w-8 h-8 text-[#fccf1f]" />
                          <span className="text-white font-medium text-sm">Cartão de Crédito</span>
                        </button>
                        <button
                          onClick={() => choosePaymentMethod("pix")}
                          className="flex flex-col items-center gap-2 p-4 rounded-lg border border-white/10 bg-zinc-900/50 hover:bg-zinc-800 hover:border-[#22c55e]/50 transition-all"
                          data-testid="select-pix-btn"
                        >
                          <QrCode className="w-8 h-8 text-[#22c55e]" />
                          <span className="text-white font-medium text-sm">PIX</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* PIX: CPF input + generate QR */}
                  {paymentMethod === "pix" && !pixData && (
                    <div className="space-y-4" data-testid="pix-cpf-form">
                      <div>
                        <Label className="text-white text-sm">CPF (obrigatório para PIX)</Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="000.000.000-00"
                          value={cardForm.cpf}
                          onChange={(e) => setCardForm({ ...cardForm, cpf: formatCPFBR(e.target.value) })}
                          className="bg-zinc-900/80 border-white/10 text-white font-mono"
                          data-testid="pix-cpf-input"
                          maxLength={14}
                        />
                        {cardCpfError && (
                          <p className="text-red-400 text-xs mt-1">{cardCpfError}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-white text-sm">CEP</Label>
                          <Input
                            type="text"
                            inputMode="numeric"
                            placeholder="00000-000"
                            value={cardForm.postal_code}
                            onChange={(e) => setCardForm({ ...cardForm, postal_code: formatCEP(e.target.value) })}
                            className="bg-zinc-900/80 border-white/10 text-white font-mono"
                            data-testid="pix-cep-input"
                            maxLength={9}
                          />
                        </div>
                        <div>
                          <Label className="text-white text-sm">Número</Label>
                          <Input
                            type="text"
                            placeholder="Nº"
                            value={cardForm.address_number}
                            onChange={(e) => setCardForm({ ...cardForm, address_number: e.target.value })}
                            className="bg-zinc-900/80 border-white/10 text-white"
                            data-testid="pix-address-number-input"
                          />
                        </div>
                      </div>
                      <p className="text-zinc-500 text-xs">
                        Esses dados são necessários para a emissão da nota fiscal.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setPaymentMethod(null)}
                          variant="outline"
                          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                          data-testid="pix-back-btn"
                        >
                          <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
                        </Button>
                        <Button
                          onClick={handlePixPayment}
                          disabled={loading}
                          className="flex-1 bg-[#22c55e] text-white font-nunito font-bold uppercase hover:bg-[#1ea750] transition-all rounded-lg"
                          data-testid="generate-pix-btn"
                        >
                          {loading ? "Gerando PIX..." : "Gerar QR Code PIX"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* PIX: QR Code Display */}
                  {pixData && (
                    <div className="space-y-4 text-center" data-testid="pix-qr-display">
                      {pixTimeLeftMs !== null && (
                        <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">Tempo restante</p>
                          <p className="mt-1 font-nunito text-2xl font-black text-amber-200">
                            {formatCountdown(pixTimeLeftMs)}
                          </p>
                          <p className="mt-1 text-xs text-amber-100/80">
                            O QR Code PIX fica válido por 1 hora.
                          </p>
                        </div>
                      )}
                      <div className="bg-white rounded-xl p-4 inline-block mx-auto">
                        <img
                          src={`data:image/png;base64,${pixData.qr_code_image}`}
                          alt="QR Code PIX"
                          className="w-48 h-48 mx-auto"
                        />
                      </div>
                      <div>
                        <p className="text-zinc-400 text-sm mb-2">Ou copie o código PIX:</p>
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            value={pixData.qr_code_payload}
                            className="bg-zinc-900/80 border-white/10 text-white text-xs font-mono"
                            data-testid="pix-payload-input"
                          />
                          <Button
                            onClick={copyPixCode}
                            variant="outline"
                            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 shrink-0"
                            data-testid="copy-pix-btn"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {pixPolling && (
                        <div className="flex items-center justify-center gap-2 text-[#22c55e]">
                          <div className="w-3 h-3 rounded-full bg-[#22c55e] animate-pulse" />
                          <span className="text-sm font-medium">Aguardando pagamento PIX...</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Credit Card Form */}
                  {paymentMethod === "card" && (
                  <>
                  <div>
                    <Label className="text-white text-sm">Número do Cartão</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="0000 0000 0000 0000"
                      value={cardForm.card_number}
                      onChange={(e) => setCardForm({ ...cardForm, card_number: formatCardNumber(e.target.value) })}
                      className="bg-zinc-900/80 border-white/10 text-white font-mono tracking-wider"
                      data-testid="card-number-input"
                      maxLength={19}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white text-sm">Validade</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="MM/AA"
                        value={cardForm.card_expiry}
                        onChange={(e) => setCardForm({ ...cardForm, card_expiry: formatExpiry(e.target.value) })}
                        className="bg-zinc-900/80 border-white/10 text-white font-mono"
                        data-testid="card-expiry-input"
                        maxLength={5}
                      />
                    </div>
                    <div>
                      <Label className="text-white text-sm">CVV</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="000"
                        value={cardForm.card_cvv}
                        onChange={(e) => setCardForm({ ...cardForm, card_cvv: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                        className="bg-zinc-900/80 border-white/10 text-white font-mono"
                        data-testid="card-cvv-input"
                        maxLength={4}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white text-sm">Nome no Cartão</Label>
                    <Input
                      type="text"
                      placeholder="NOME COMO ESTÁ NO CARTÃO"
                      value={cardForm.card_holder_name}
                      onChange={(e) => setCardForm({ ...cardForm, card_holder_name: e.target.value.toUpperCase() })}
                      className="bg-zinc-900/80 border-white/10 text-white uppercase"
                      data-testid="card-holder-input"
                    />
                  </div>
                  <div className="border-t border-white/10 pt-4">
                    <p className="text-zinc-400 text-xs mb-3">Dados do titular (obrigatório)</p>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-white text-sm">CPF</Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="000.000.000-00"
                          value={cardForm.cpf}
                          onChange={(e) => setCardForm({ ...cardForm, cpf: formatCPFBR(e.target.value) })}
                          className="bg-zinc-900/80 border-white/10 text-white font-mono"
                          data-testid="card-cpf-input"
                          maxLength={14}
                        />
                        {cardCpfError && (
                          <p className="text-red-400 text-xs mt-1">{cardCpfError}</p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-white text-sm">CEP</Label>
                          <Input
                            type="text"
                            inputMode="numeric"
                            placeholder="00000-000"
                            value={cardForm.postal_code}
                            onChange={(e) => setCardForm({ ...cardForm, postal_code: formatCEP(e.target.value) })}
                            className="bg-zinc-900/80 border-white/10 text-white font-mono"
                            data-testid="card-cep-input"
                            maxLength={9}
                          />
                        </div>
                        <div>
                          <Label className="text-white text-sm">Número</Label>
                          <Input
                            type="text"
                            placeholder="Nº"
                            value={cardForm.address_number}
                            onChange={(e) => setCardForm({ ...cardForm, address_number: e.target.value })}
                            className="bg-zinc-900/80 border-white/10 text-white"
                            data-testid="card-address-number-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={() => { setPaymentMethod(null); setPaymentResult(null); }}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      disabled={loading}
                      data-testid="back-to-method-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
                    </Button>
                    <Button
                      onClick={handleCardPayment}
                      disabled={loading}
                      className="flex-1 bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c] transition-all rounded-lg"
                      data-testid="pay-card-btn"
                    >
                      {loading ? "Processando..." : `Pagar R$ ${formatCurrencyBR(pendingBookingTotal ?? pricing?.total ?? 0)}`}
                    </Button>
                  </div>

                  <p className="text-zinc-600 text-[10px] text-center">
                    Pagamento processado com segurança via Asaas. Seus dados são criptografados.
                  </p>
                  </>
                  )}
                  {/* End Credit Card Form */}

                  {/* Back to booking button (when no method selected yet) */}
                  {!paymentMethod && (
                    <Button
                      onClick={() => { setPaymentStep(false); setPaymentResult(null); }}
                      variant="outline"
                      className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      data-testid="back-to-booking-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" /> Voltar aos dados da reserva
                    </Button>
                  )}
                </div>
              )}

              {/* Booking Step - Original Form (hidden when in payment step) */}
              {!paymentStep && (
              <>
              {ticketTypes.length === 0 ? (
                <div className="text-center py-8 bg-zinc-800/30 rounded-lg" data-testid="no-tickets-message">
                  <Ticket className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400 font-outfit text-base font-medium">
                    Não há ingressos disponíveis para esse evento.
                  </p>
                  <p className="text-zinc-500 text-sm mt-1">
                    Os ingressos ainda não foram configurados para este evento.
                  </p>
                </div>
              ) : (
              <>
              {selectedTicketType?.quantity_pricing?.length > 0 && (
                <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Tag size={16} className="text-[#6e46d6] dark:text-purple-300" />
                        <p className="text-sm font-semibold text-[#6e46d6] dark:text-purple-200">
                          Desconto progressivo por quantidade
                        </p>
                      </div>
                      <div className="text-xs text-zinc-300 space-y-1">
                        <p>Quanto mais ingressos, melhor o valor por pessoa.</p>
                        <p>A melhor faixa é aplicada automaticamente.</p>
                      </div>
                    </div>
                    {pricing?.quantityPricingRule && Number(pricing.quantitySavings || 0) > 0 && (
                      <div className="rounded-full bg-[#fccf1f]/15 text-[#fccf1f] px-3 py-1 text-xs font-semibold uppercase tracking-wide w-fit shrink-0 whitespace-nowrap self-start md:self-center">
                        Economize R$ {formatCurrencyBR(pricing.quantitySavings)}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    {selectedTicketType.quantity_pricing.map((rule, idx) => {
                      const activeRule = pricing?.quantityPricingRule;
                      const isActive = activeRule && activeRule.min_quantity === rule.min_quantity && activeRule.rule_type === rule.rule_type && Number(activeRule.value || 0) === Number(rule.value || 0);
                      return (
                        <div
                          key={`selected-ticket-rule-top-${idx}`}
                          className={`rounded-xl px-4 py-3 text-sm border transition-colors ${isActive ? "bg-purple-500/20 border-purple-300/40 text-white shadow-[0_0_0_1px_rgba(196,181,253,0.15)]" : "bg-zinc-900/40 border-white/10 text-zinc-300"}`}
                        >
                          <div className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between sm:gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold">{rule.label || describeQuantityPricingRule(rule)}</p>
                              {rule.label && (
                                <p className="text-xs text-zinc-400 mt-1">{describeQuantityPricingRule(rule)}</p>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                              {idx === selectedTicketType.quantity_pricing.length - 1 && (
                                <span className="text-[10px] uppercase tracking-wide bg-[#fccf1f]/15 text-[#fccf1f] px-2 py-1 rounded-full">
                                  Melhor oferta
                                </span>
                              )}
                              {isActive && <span className="text-xs font-semibold text-purple-200">Aplicado</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px] gap-4 items-start"><div>
                <Label className="text-white mb-2 block">Tipo de Ingresso</Label>
                <Select
                  value={selectedTicketType?.type}
                  onValueChange={(value) => {
                    const type = ticketTypes.find(t => t.type === value);
                    setSelectedTicketType(type);
                    setBookingForm({ ...bookingForm, quantity: type.min_quantity || 1 });
                  }}
                >
                  <SelectTrigger className="bg-zinc-900/80 border-white/10 text-white">
                    <SelectValue placeholder="Selecione o tipo de ingresso" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {ticketTypes.map((type) => (
                      <SelectItem key={type.type} value={type.type} className="text-white">
                        {type.name} - R$ {formatCurrencyValue(type.price)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTicketType && (
                  <div className="mt-2 space-y-3">
                    {hasActiveSubscription(user) && pricing?.subscriptionBenefitApplied && (
                      <div className="p-3 bg-[#fccf1f]/10 border border-[#fccf1f]/20 rounded-lg">
                        <p className="text-sm text-[#fccf1f] flex items-start">
                          <Ticket size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                          <span>
                            Seu plano aplicará <strong>{pricing.subscriptionBenefitLabel}</strong> neste checkout, mantendo o primeiro ingresso por <strong>R$ {formatCurrencyBR(pricing.subscriptionBenefitFirstLotPrice)}</strong> e somando esse benefício ao desconto progressivo por quantidade, quando houver.
                          </span>
                        </p>
                      </div>
                    )}
                    {!hasActiveSubscription(user) && selectedTicketType.has_first_lot_benefit && (
                      <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                        <p className="text-sm text-purple-300 flex items-start">
                          <Info size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                          <span>
                            Assinantes economizam mais: no clube, o primeiro ingresso mantém preço de 1º lote mesmo após a virada e ainda acumula com o desconto progressivo por quantidade, quando aplicável.
                          </span>
                        </p>
                      </div>
                    )}
                    {selectedTicketType.description && (
                      <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <p className="text-sm text-blue-400 flex items-start">
                          <Info size={16} className="mr-2 mt-0.5 flex-shrink-0" />
                          <span>{selectedTicketType.description}</span>
                        </p>
                      </div>
                    )}
                    {false && selectedTicketType.quantity_pricing?.length > 0 && (
                      <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between mb-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Tag size={16} className="text-[#6e46d6] dark:text-purple-300" />
                              <p className="text-sm font-semibold text-[#6e46d6] dark:text-purple-200">
                                Desconto progressivo por quantidade
                              </p>
                            </div>
                            <div className="text-xs text-zinc-300 space-y-1">
                              <p>Quanto mais ingressos, melhor o valor por pessoa.</p>
                              <p>A melhor faixa é aplicada automaticamente.</p>
                            </div>
                          </div>
                          {pricing?.quantityPricingRule && Number(pricing.quantitySavings || 0) > 0 && (
                            <div className="rounded-full bg-[#fccf1f]/15 text-[#fccf1f] px-3 py-1 text-xs font-semibold uppercase tracking-wide w-fit shrink-0 whitespace-nowrap self-start md:self-center">
                              Economize R$ {formatCurrencyBR(pricing.quantitySavings)}
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          {selectedTicketType.quantity_pricing.map((rule, idx) => {
                            const activeRule = pricing?.quantityPricingRule;
                            const isActive = activeRule && activeRule.min_quantity === rule.min_quantity && activeRule.rule_type === rule.rule_type && Number(activeRule.value || 0) === Number(rule.value || 0);
                            return (
                              <div
                                key={`selected-ticket-rule-${idx}`}
                                className={`rounded-xl px-4 py-3 text-sm border transition-colors ${isActive ? "bg-purple-500/20 border-purple-300/40 text-white shadow-[0_0_0_1px_rgba(196,181,253,0.15)]" : "bg-zinc-900/40 border-white/10 text-zinc-300"}`}
                              >
                                <div className="space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between sm:gap-3">
                                  <div className="min-w-0">
                                    <p className="font-semibold">{rule.label || describeQuantityPricingRule(rule)}</p>
                                    {rule.label && (
                                      <p className="text-xs text-zinc-400 mt-1">{describeQuantityPricingRule(rule)}</p>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                                    {idx === selectedTicketType.quantity_pricing.length - 1 && (
                                      <span className="text-[10px] uppercase tracking-wide bg-[#fccf1f]/15 text-[#fccf1f] px-2 py-1 rounded-full">
                                        Melhor oferta
                                      </span>
                                    )}
                                    {isActive && <span className="text-xs font-semibold text-purple-200">Aplicado</span>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!isOnlineGameEvent(selectedEvent) ? (
              <div>
                <Label className="text-white mb-2 block">Quantidade</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(Math.max((parseInt(bookingForm.quantity) || 1) - 1, selectedTicketType?.min_quantity || 1))}
                    disabled={(parseInt(bookingForm.quantity) || 1) <= (selectedTicketType?.min_quantity || 1)}
                    className="h-10 w-10 border-zinc-700 text-white hover:bg-zinc-800"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={bookingForm.quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    onBlur={validateQuantityOnBlur}
                    className={`bg-zinc-900/80 border-white/10 text-white text-center w-20 ${getQuantityError() ? 'border-red-500' : ''}`}
                    data-testid="booking-quantity-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange((parseInt(bookingForm.quantity) || 0) + 1)}
                    disabled={selectedTicketType?.max_quantity && (parseInt(bookingForm.quantity) || 0) >= selectedTicketType.max_quantity}
                    className="h-10 w-10 border-zinc-700 text-white hover:bg-zinc-800"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {getQuantityError() && (
                  <p className="text-red-400 text-xs mt-1">{getQuantityError()}</p>
                )}
                {(selectedTicketType?.min_quantity || selectedTicketType?.max_quantity) && !getQuantityError() && (
                  <p className="text-zinc-500 text-xs mt-1">
                    {selectedTicketType.min_quantity && `Mínimo: ${selectedTicketType.min_quantity}`}
                    {selectedTicketType.min_quantity && selectedTicketType.max_quantity && " • "}
                    {selectedTicketType.max_quantity && `Máximo: ${selectedTicketType.max_quantity}`}
                  </p>
                )}
              </div>
              ) : (
              <div>
                <Label className="text-white mb-2 block">Quantidade</Label>
                <div className="flex h-10 items-center justify-center rounded-md border border-white/10 bg-zinc-900/80 text-center text-white">
                  1 ingresso individual
                </div>
                <p className="text-zinc-500 text-xs mt-1">
                  Eventos online aceitam somente um ingresso por usuário logado.
                </p>
              </div>
              )}
              </div>

              <div>
                <Label className="text-white">Nome Completo</Label>
                <Input
                  type="text"
                  value={bookingForm.name}
                  onChange={(e) =>
                    setBookingForm({ ...bookingForm, name: e.target.value })
                  }
                  className="bg-zinc-900/80 border-white/10 text-white"
                  data-testid="booking-name-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">E-mail</Label>
                  <Input
                    type="email"
                    value={bookingForm.email}
                    onChange={(e) =>
                      setBookingForm({ ...bookingForm, email: e.target.value })
                    }
                    className="bg-zinc-900/80 border-white/10 text-white"
                    data-testid="booking-email-input"
                    disabled={isOnlineGameEvent(selectedEvent) && !!user?.email}
                  />
                  {isOnlineGameEvent(selectedEvent) && user?.email ? (
                    <p className="text-zinc-500 text-xs mt-1">
                      Este ingresso será vinculado ao login <strong className="text-zinc-300">{user.email}</strong>.
                    </p>
                  ) : null}
                  {bookingEmailError && (
                    <p className="text-red-400 text-xs mt-1">{bookingEmailError}</p>
                  )}
                </div>

                <div>
                  <Label className="text-white">Celular <span className="text-red-500">*</span></Label>
                  <Input
                    type="tel"
                    placeholder="(11) 99999-9999"
                    value={bookingForm.phone}
                    onChange={(e) =>
                      setBookingForm({ ...bookingForm, phone: formatPhoneBR(e.target.value) })
                    }
                    className="bg-zinc-900/80 border-white/10 text-white"
                    data-testid="booking-phone-input"
                    required
                  />
                  {bookingPhoneError && (
                    <p className="text-red-400 text-xs mt-1">{bookingPhoneError}</p>
                  )}
                </div>
              </div>

              {!isOnlineGameEvent(selectedEvent) && (
                <div className="rounded-xl border border-white/10 bg-zinc-800/40 p-4 space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bookingForm.has_pcd}
                      onChange={(e) =>
                        setBookingForm((prev) => ({
                          ...prev,
                          has_pcd: e.target.checked,
                          accessibility_needs: e.target.checked ? prev.accessibility_needs : "",
                        }))
                      }
                      className="mt-1 h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-[#fccf1f] focus:ring-[#fccf1f]"
                      data-testid="booking-has-pcd-checkbox"
                    />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">Há PCD entre os jogadores deste pedido</span>
                        {selectedVenueAccessibilityInfo && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[#fccf1f]/30 text-[#fccf1f] hover:bg-[#fccf1f]/10"
                                aria-label="Ver informações de acessibilidade do local"
                              >
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-sm text-left">
                              <p className="font-semibold mb-1">Acessibilidade do local</p>
                              <p>{selectedVenueAccessibilityInfo}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <p className="text-sm text-zinc-400 mt-1">
                        Se houver alguma necessidade específica, descreva abaixo. Para mais informações sobre acessibilidade ou para solicitar alguma adaptação, entre em contato diretamente com o local do evento.
                      </p>
                    </div>
                  </label>

                  {bookingForm.has_pcd && (
                    <div>
                      <Label className="text-white">Necessidade especial (opcional)</Label>
                      <textarea
                        value={bookingForm.accessibility_needs}
                        onChange={(e) =>
                          setBookingForm((prev) => ({ ...prev, accessibility_needs: e.target.value }))
                        }
                        className="mt-2 w-full min-h-[96px] rounded-md border border-white/10 bg-zinc-900/80 px-3 py-2 text-sm text-white outline-none placeholder:text-zinc-500"
                        placeholder="Ex: necessidade de mesa acessível, apoio para locomoção, espaço para cadeira de rodas, acompanhante, etc."
                        data-testid="booking-accessibility-needs-input"
                      />
                      {selectedVenueAccessibilityInfo && (
                        <div className="mt-2 rounded-lg border border-[#fccf1f]/20 bg-[#fccf1f]/10 p-3 text-sm text-zinc-200">
                          <p className="font-semibold text-[#fccf1f] mb-1">Informações de acessibilidade deste local</p>
                          <p>{selectedVenueAccessibilityInfo}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {false && (
              <div>
                <Label className="text-white">Quantidade de Ingressos</Label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(Math.max((parseInt(bookingForm.quantity) || 1) - 1, selectedTicketType?.min_quantity || 1))}
                    disabled={(parseInt(bookingForm.quantity) || 1) <= (selectedTicketType?.min_quantity || 1)}
                    className="h-10 w-10 border-zinc-700 text-white hover:bg-zinc-800"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={bookingForm.quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    onBlur={validateQuantityOnBlur}
                    className={`bg-zinc-900/80 border-white/10 text-white text-center w-20 ${getQuantityError() ? 'border-red-500' : ''}`}
                    data-testid="booking-quantity-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange((parseInt(bookingForm.quantity) || 0) + 1)}
                    disabled={selectedTicketType?.max_quantity && (parseInt(bookingForm.quantity) || 0) >= selectedTicketType.max_quantity}
                    className="h-10 w-10 border-zinc-700 text-white hover:bg-zinc-800"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {getQuantityError() && (
                  <p className="text-red-400 text-xs mt-1">{getQuantityError()}</p>
                )}
                {(selectedTicketType?.min_quantity || selectedTicketType?.max_quantity) && !getQuantityError() && (
                  <p className="text-zinc-500 text-xs mt-1">
                    {selectedTicketType.min_quantity && `Mínimo: ${selectedTicketType.min_quantity}`}
                    {selectedTicketType.min_quantity && selectedTicketType.max_quantity && " • "}
                    {selectedTicketType.max_quantity && `Máximo: ${selectedTicketType.max_quantity}`}
                  </p>
                )}
              </div>
              )}

              {/* Name Individually Checkbox */}
              {!isOnlineGameEvent(selectedEvent) && bookingForm.quantity > 1 && (
                <div className="flex items-center space-x-2 bg-zinc-800/50 p-3 rounded-lg">
                  <input
                    type="checkbox"
                    id="name-individually"
                    checked={bookingForm.name_individually}
                    onChange={(e) => handleNameIndividuallyChange(e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-[#fccf1f] focus:ring-[#fccf1f]"
                    data-testid="name-individually-checkbox"
                  />
                  <Label htmlFor="name-individually" className="text-zinc-300 text-sm cursor-pointer">
                    Nomear os ingressos individualmente
                  </Label>
                </div>
              )}

              {/* Individual Attendee Fields */}
              {!isOnlineGameEvent(selectedEvent) && bookingForm.name_individually && bookingForm.quantity > 1 && (
                <div className="space-y-4 bg-zinc-800/30 p-4 rounded-lg border border-white/5">
                  <p className="text-sm text-zinc-400 font-outfit">
                    Preencha os dados de cada convidado:
                  </p>
                  {bookingForm.attendees.map((attendee, index) => (
                    <div key={index} className="bg-zinc-900/50 p-3 rounded-lg space-y-2">
                      <p className="text-[#fccf1f] font-bold text-sm">Ingresso {index + 1}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-zinc-400 text-xs">Nome</Label>
                          <Input
                            type="text"
                            placeholder="Nome completo"
                            value={attendee.name}
                            onChange={(e) => handleAttendeeChange(index, "name", e.target.value)}
                            className="bg-zinc-800 border-white/10 text-white text-sm"
                            data-testid={`attendee-name-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-400 text-xs">E-mail</Label>
                          <Input
                            type="email"
                            placeholder="email@exemplo.com"
                            value={attendee.email}
                            onChange={(e) => handleAttendeeChange(index, "email", e.target.value)}
                            className="bg-zinc-800 border-white/10 text-white text-sm"
                            data-testid={`attendee-email-${index}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {pricing && (
                <div className="bg-zinc-800/50 p-4 sm:p-6 rounded-lg space-y-3">
                  <div className="flex justify-between text-zinc-300">
                    <span>
                      Subtotal ({pricing.quantity}x R$ {Number(pricing.baseUnitPrice || 0).toFixed(2)})
                    </span>
                    <span>R$ {pricing.baseSubtotal}</span>
                  </div>

                  {Number(pricing.quantitySavings || 0) > 0 && (
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-emerald-300 font-semibold">Desconto progressivo aplicado</p>
                          <p className="text-sm text-zinc-300 mt-1">
                            {describeAppliedQuantityPricing(pricing.quantityPricingRule, pricing.unitPrice)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs uppercase tracking-wide text-zinc-400">Desconto</p>
                          <p className="text-emerald-300 font-bold text-lg">- R$ {pricing.quantitySavings}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {pricing.subscriptionBenefitApplied && Number(pricing.subscriptionBenefitDiscount || 0) > 0 && (
                    <div className="rounded-xl border border-[#fccf1f]/20 bg-[#fccf1f]/10 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className="text-[#fccf1f] font-semibold">{pricing.subscriptionBenefitLabel}</p>
                          <p className="text-sm text-zinc-300 mt-1">
                            O primeiro ingresso foi recalculado com a tabela do 1º lote, mantendo também o desconto progressivo por quantidade quando ele existir.
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs uppercase tracking-wide text-zinc-400">Benefício</p>
                          <p className="text-[#fccf1f] font-bold text-lg">- R$ {pricing.subscriptionBenefitDiscount}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Ticket Credits Selector */}
                  {canApplyTicketCredit && (
                    <div className="border-t border-white/10 pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-outfit text-sm text-zinc-300 flex items-center gap-2">
                          <Ticket className="w-4 h-4 text-purple-400" />
                          Créditos disponíveis: <strong className="text-purple-300">{user.ticket_credits}</strong>
                        </span>
                        {(normalizedRemainingCreditsForEvent !== null || normalizedPerEventCreditLimit !== null) && (
                          <span className="text-xs text-zinc-500">
                            Restante neste evento: <strong className="text-zinc-300">{normalizedRemainingCreditsForEvent ?? normalizedPerEventCreditLimit}</strong>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between bg-zinc-900/60 border border-white/10 rounded-lg p-3">
                        <span className="font-outfit text-sm text-zinc-400">Usar créditos:</span>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setCreditsToApply(Math.max(0, creditsToApply - 1))}
                            disabled={creditsToApply <= 0}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800 border border-white/10 text-white disabled:opacity-30 hover:border-purple-500/50 transition-colors"
                            data-testid="credits-decrease-btn"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="font-nunito font-bold text-lg text-purple-300 w-8 text-center" data-testid="credits-count">
                            {creditsToApply}
                          </span>
                          <button
                            onClick={() => setCreditsToApply(Math.min(maxCredits, creditsToApply + 1))}
                            disabled={creditsToApply >= maxCredits}
                            className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800 border border-white/10 text-white disabled:opacity-30 hover:border-purple-500/50 transition-colors"
                            data-testid="credits-increase-btn"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {creditsToApply > 0 && (
                        <div className="flex justify-between text-purple-300 text-sm mt-2">
                          <span>Desconto ({creditsToApply} crédito{creditsToApply !== 1 ? "s" : ""})</span>
                          <span>- R$ {pricing.creditDiscount}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {teamCreditOptions.length > 0 && (
                    <div className="border-t border-white/10 pt-3 space-y-2" data-testid="team-credits-selector">
                      <div>
                        <p className="font-outfit text-sm text-zinc-200">Créditos da equipe</p>
                        <p className="font-outfit text-xs text-zinc-500 mt-1">Apenas créditos de assinatura autorizados pelos integrantes aparecem aqui.</p>
                      </div>
                      {teamCreditOptions.map((member) => {
                        const selected = Number(teamCreditUses[member.user_id] || 0);
                        const remainingSlots = Math.max(0, (parseInt(bookingForm.quantity) || 1) - creditsToApply - teamCreditsToApply);
                        return (
                          <div key={member.user_id} className="flex items-center justify-between gap-3 bg-zinc-900/60 border border-white/10 rounded-lg p-3">
                            <div className="min-w-0">
                              <p className="font-outfit text-sm text-zinc-200 truncate">{member.is_current_user ? "Meus créditos de assinatura" : member.name}</p>
                              <p className="font-outfit text-xs text-purple-300">{member.available_credits} disponível{member.available_credits !== 1 ? "is" : ""}</p>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <button type="button" onClick={() => setTeamCreditUses((current) => ({ ...current, [member.user_id]: Math.max(0, selected - 1) }))} disabled={selected <= 0} className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800 border border-white/10 text-white disabled:opacity-30 hover:border-purple-500/50 transition-colors">
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="font-nunito font-bold text-lg text-purple-300 w-5 text-center">{selected}</span>
                              <button type="button" onClick={() => setTeamCreditUses((current) => ({ ...current, [member.user_id]: Math.min(member.available_credits, selected + 1) }))} disabled={selected >= member.available_credits || remainingSlots <= 0} className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-800 border border-white/10 text-white disabled:opacity-30 hover:border-purple-500/50 transition-colors">
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {teamCreditsToApply > 0 && (
                        <div className="flex justify-between text-purple-300 text-sm">
                          <span>Créditos de equipe ({teamCreditsToApply})</span>
                          <span>- R$ {(Number(pricing?.unitPrice || 0) * teamCreditsToApply).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fees breakdown - only show if paying */}
                  {parseFloat(pricing.total) > 0 && (
                    <div className="border-t border-white/10 pt-3 space-y-1">
                      <div className="flex justify-between text-zinc-400 text-sm">
                        <span>Taxa de serviço{pricing.feeLabel ? ` (${pricing.feeLabel})` : ""}</span>
                        <span>R$ {pricing.fees}</span>
                      </div>
                    </div>
                  )}

                  {/* Coupon Code - show if paying and no credits zeroing out */}
                  {parseFloat(pricing.paidSubtotal) > 0 || couponResult ? (
                    <div className="border-t border-white/10 pt-3">
                      {couponResult ? (
                        <div className="flex items-center justify-between bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <Check className="w-4 h-4 text-green-400" />
                            <span className="font-outfit text-sm text-green-400">
                              Cupom <strong>{couponResult.coupon.code}</strong>
                              {couponResult.coupon.discount_type === "percentage"
                                ? ` (${couponResult.coupon.discount_value}%)`
                                : ""}: -R$ {pricing.couponDiscount}
                            </span>
                          </div>
                          <button onClick={removeCoupon} className="text-zinc-500 hover:text-red-400 text-xs font-outfit">
                            Remover
                          </button>
                        </div>
                      ) : parseFloat(pricing.paidSubtotal) > 0 ? (
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                            <Input
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              placeholder="Cupom de desconto"
                              className="pl-9 bg-zinc-900/80 border-white/10 text-white uppercase font-mono h-9 text-sm"
                              data-testid="coupon-input"
                            />
                          </div>
                          <Button
                            onClick={validateCoupon}
                            disabled={!couponCode.trim() || validatingCoupon}
                            size="sm"
                            variant="outline"
                            className="border-[#fccf1f]/30 text-[#fccf1f] hover:bg-[#fccf1f]/10 h-9"
                            data-testid="apply-coupon-btn"
                          >
                            {validatingCoupon ? "..." : "Aplicar"}
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="border-t border-white/10 pt-3 flex justify-between">
                    <span className="font-outfit text-white font-bold">Total a pagar:</span>
                    <span className="font-nunito font-bold text-2xl text-[#fccf1f]" data-testid="checkout-total">
                      {parseFloat(pricing.total) === 0 ? (
                        <span className="flex items-center gap-2">
                          <span className="text-sm text-zinc-500 line-through">R$ {pricing.subtotal}</span>
                          R$ 0,00
                        </span>
                      ) : (
                        `R$ ${pricing.total}`
                      )}
                    </span>
                  </div>
                </div>
              )}

              <Button
                onClick={handleBooking}
                disabled={loading}
                className="w-full bg-[#fccf1f] text-black font-nunito font-bold uppercase hover:bg-[#e6bc1c] transition-all rounded-lg py-3"
                data-testid="confirm-booking-btn"
              >
                {loading ? "Processando..." : parseFloat(pricing?.total || "1") === 0 ? "Confirmar Pedido" : "Ir para Pagamento"}
              </Button>
              </>
              )}
              </>
              )}
            </div>
            </TooltipProvider>
          )}
        </DialogContent>
      </Dialog>
      {/* Event Sales Dashboard */}
      <EventSalesDashboard eventId={showEventSales} open={!!showEventSales} onClose={() => setShowEventSales(null)} />
      
      <EventEditorDialog
        open={showEditEvent}
        onOpenChange={(open) => {
          if (!open) {
            setShowEditEvent(false);
            setEditingEvent(null);
            setEditForm(null);
          } else {
            setShowEditEvent(true);
          }
        }}
        event={editingEvent}
        onSaved={() => {
          fetchEvents();
          if (detailsEvent?.id && editingEvent?.id === detailsEvent.id) {
            openEventBySlug(editingEvent.slug || detailsEvent.slug);
          }
        }}
      />

      {/* Legacy Edit Event Modal kept disabled */}
      {false && <Dialog open={showEditEvent} onOpenChange={(open) => { if (!open) { setShowEditEvent(false); setEditingEvent(null); setEditForm(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-white/10" data-testid="edit-event-modal">
          <DialogHeader>
            <DialogTitle className="text-white font-nunito text-xl flex items-center gap-2">
              <Pencil className="w-5 h-5 text-orange-400" />
              Editar Evento
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {editingEvent?.title}
            </DialogDescription>
          </DialogHeader>
          
          {editForm && (
            <div className="space-y-4 py-2">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-zinc-300 text-sm">Título</Label>
                  <Input
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="edit-event-title"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label className="text-zinc-300 text-sm">Descrição</Label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-md p-2 min-h-[80px]"
                    data-testid="edit-event-description"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-300 text-sm">Data</Label>
                  <Input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="edit-event-date"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-300 text-sm">Horário</Label>
                  <Input
                    type="time"
                    value={editForm.time}
                    onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="edit-event-time"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-300 text-sm">Local</Label>
                  <Select value={editForm.venue_id} onValueChange={(v) => {
                    const venue = venues.find(ve => ve.id === v);
                    setEditForm({
                      ...editForm,
                      venue_id: v,
                      location: venue?.name || "",
                      address: venue?.address || "",
                      maps_url: venue?.maps_url || ""
                    });
                  }}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Selecionar local" />
                    </SelectTrigger>
                    <SelectContent>
                      {venues.filter(v => v.is_active !== false).map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-300 text-sm">Tipo de Evento</Label>
                  <Select value={editForm.event_type} onValueChange={(v) => setEditForm({ ...editForm, event_type: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="presencial">Presencial</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="hybrid">Híbrido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-300 text-sm">Ingressos Disponíveis</Label>
                  <Input
                    type="number"
                    value={editForm.tickets_available}
                    onChange={(e) => setEditForm({ ...editForm, tickets_available: parseInt(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="edit-event-tickets"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-300 text-sm">Preço Base (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.price}
                    onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="edit-event-price"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label className="text-zinc-300 text-sm">URL da Imagem</Label>
                  <Input
                    value={editForm.image_url}
                    onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                    placeholder="https://..."
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="edit-event-image"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label className="text-zinc-300 text-sm">Descrição do Prêmio</Label>
                  <Input
                    value={editForm.prize_description}
                    onChange={(e) => setEditForm({ ...editForm, prize_description: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="edit-event-prize"
                  />
                </div>
              </div>
              
              {/* Ticket Tiers */}
              {editForm.requires_ticket && editForm.ticket_tiers?.length > 0 && (
                <div className="border-t border-zinc-700 pt-4">
                  <Label className="text-zinc-300 text-sm mb-2 block">Lotes de Ingressos</Label>
                  <div className="space-y-3">
                    {editForm.ticket_tiers.map((tier, idx) => (
                      <div key={idx} className="bg-zinc-800/50 p-3 rounded-lg border border-zinc-700">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <Input
                            value={tier.name}
                            onChange={(e) => {
                              const newTiers = [...editForm.ticket_tiers];
                              newTiers[idx] = { ...tier, name: e.target.value };
                              setEditForm({ ...editForm, ticket_tiers: newTiers });
                            }}
                            placeholder="Nome do lote"
                            className="bg-zinc-900 border-zinc-600 text-white text-sm"
                          />
                          <Input
                            type="number"
                            step="0.01"
                            value={tier.price}
                            onChange={(e) => {
                              const newTiers = [...editForm.ticket_tiers];
                              newTiers[idx] = { ...tier, price: parseFloat(e.target.value) || 0 };
                              setEditForm({ ...editForm, ticket_tiers: newTiers });
                            }}
                            placeholder="Preço"
                            className="bg-zinc-900 border-zinc-600 text-white text-sm"
                          />
                          <Input
                            type="datetime-local"
                            value={tier.start_date?.slice(0, 16) || ""}
                            onChange={(e) => {
                              const newTiers = [...editForm.ticket_tiers];
                              newTiers[idx] = { ...tier, start_date: e.target.value };
                              setEditForm({ ...editForm, ticket_tiers: newTiers });
                            }}
                            placeholder="Início"
                            className="bg-zinc-900 border-zinc-600 text-white text-sm"
                          />
                          <Input
                            type="datetime-local"
                            value={tier.end_date?.slice(0, 16) || ""}
                            onChange={(e) => {
                              const newTiers = [...editForm.ticket_tiers];
                              newTiers[idx] = { ...tier, end_date: e.target.value };
                              setEditForm({ ...editForm, ticket_tiers: newTiers });
                            }}
                            placeholder="Fim"
                            className="bg-zinc-900 border-zinc-600 text-white text-sm"
                          />
                        </div>
                        
                        {/* Advanced tier options */}
                        <div className="mt-3 pt-3 border-t border-zinc-700/50">
                          <p className="text-xs text-zinc-500 mb-2">Opções avançadas de término:</p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                value={tier.end_before_value || ""}
                                onChange={(e) => {
                                  const newTiers = [...editForm.ticket_tiers];
                                  newTiers[idx] = { ...tier, end_before_value: parseInt(e.target.value) || null };
                                  setEditForm({ ...editForm, ticket_tiers: newTiers });
                                }}
                                placeholder="Número"
                                className="bg-zinc-900 border-zinc-600 text-white text-sm w-20"
                              />
                              <Select 
                                value={tier.end_before_unit || ""} 
                                onValueChange={(v) => {
                                  const newTiers = [...editForm.ticket_tiers];
                                  newTiers[idx] = { ...tier, end_before_unit: v };
                                  setEditForm({ ...editForm, ticket_tiers: newTiers });
                                }}
                              >
                                <SelectTrigger className="bg-zinc-900 border-zinc-600 text-white text-xs w-24">
                                  <SelectValue placeholder="Unidade" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="minutes">Minutos</SelectItem>
                                  <SelectItem value="hours">Horas</SelectItem>
                                  <SelectItem value="days">Dias</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select 
                                value={tier.end_before_reference || ""} 
                                onValueChange={(v) => {
                                  const newTiers = [...editForm.ticket_tiers];
                                  newTiers[idx] = { ...tier, end_before_reference: v };
                                  setEditForm({ ...editForm, ticket_tiers: newTiers });
                                }}
                              >
                                <SelectTrigger className="bg-zinc-900 border-zinc-600 text-white text-xs flex-1">
                                  <SelectValue placeholder="Antes de..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="event_start">Início do evento</SelectItem>
                                  <SelectItem value="event_end">Fim do evento</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <p className="text-xs text-zinc-500 mt-3 mb-2">Início alternativo (virar quando esgotar):</p>
                          <Select 
                            value={tier.start_after_tier || ""} 
                            onValueChange={(v) => {
                              const newTiers = [...editForm.ticket_tiers];
                              newTiers[idx] = { ...tier, start_after_tier: v };
                              setEditForm({ ...editForm, ticket_tiers: newTiers });
                            }}
                          >
                            <SelectTrigger className="bg-zinc-900 border-zinc-600 text-white text-xs w-full md:w-64">
                              <SelectValue placeholder="Iniciar quando outro lote esgotar" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Nenhum (usar data de início)</SelectItem>
                              {editForm.ticket_tiers
                                .filter((_, i) => i !== idx)
                                .map((t, i) => (
                                  <SelectItem key={i} value={t.name || `tier_${i}`}>{t.name} esgotar</SelectItem>
                                ))
                              }
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                    
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditForm({
                        ...editForm,
                        ticket_tiers: [...editForm.ticket_tiers, { name: `${editForm.ticket_tiers.length + 1}º Lote`, price: 0, start_date: "", end_date: "" }]
                      })}
                      className="border-zinc-600 text-zinc-300"
                    >
                      <PlusCircle className="w-4 h-4 mr-1" /> Adicionar Lote
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Toggles */}
              <div className="border-t border-zinc-700 pt-4 flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.requires_ticket}
                    onChange={(e) => setEditForm({ ...editForm, requires_ticket: e.target.checked })}
                    className="w-4 h-4 rounded border-zinc-600"
                  />
                  <span className="text-sm text-zinc-300">Requer Ingresso</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.use_custom_tickets}
                    onChange={(e) => setEditForm({ ...editForm, use_custom_tickets: e.target.checked })}
                    className="w-4 h-4 rounded border-zinc-600"
                  />
                  <span className="text-sm text-zinc-300">Ingressos Customizados</span>
                </label>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t border-zinc-700">
                <Button
                  variant="outline"
                  onClick={() => { setShowEditEvent(false); setEditingEvent(null); setEditForm(null); }}
                  className="border-zinc-600 text-zinc-300"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid="save-edit-event-btn"
                >
                  {savingEdit ? "Salvando..." : "Salvar Alterações"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>}
    </div>
  );
}
